/* ============================================================================
   TENDER ROOM — domain model
   A Tender is the aggregate root: documents, bidders, bids, RFIs, timeline,
   the imported draft contract and the alert log all live on it.
   ========================================================================== */

export type Role = "manager" | "client" | "bidder";

/** Lifecycle: draft → open → (close date passes) → evaluation → awarded.
    "Closed" is derived from the close date, not stored. */
export type TenderStatus = "draft" | "open" | "evaluation" | "awarded";

export interface Party {
  name: string;         // contact person
  company: string;
  email: string;
  phone?: string;
}

export type EventKind =
  | "issue" | "site-visit" | "rfi-deadline" | "addendum"
  | "close" | "evaluation" | "award" | "contract";

export interface TimelineEvent {
  id: string;
  label: string;
  date: string;          // ISO
  kind: EventKind;
  note?: string;
}

export type DocCategory = "drawing" | "specification" | "boq" | "form" | "addendum" | "contract";

export interface TenderDoc {
  id: string;
  number: string;        // A-101, SPEC-09, ADD-01 …
  title: string;
  discipline: string;    // Architectural / Structural / MEP / Commercial …
  revision: string;
  category: DocCategory;
  size: string;          // display only — "4.2 MB"
  issuedAt: string;
  filePage?: number;     // page inside the original file (combined-set uploads)
  previewId?: string;    // rendered page image in the sheet store (real preview)
  fileId?: string;       // original file in R2 — enables real download
  fileName?: string;
  /** This sheet's OWN one-page PDF, split from a combined upload — what a
      download hands over, and what the viewer renders at full quality,
      instead of the whole multi-sheet original. */
  pageFileId?: string;
  /** Withdrawn from use — stamped wherever it is drawn. */
  obsolete?: boolean;
  /** The instruction itself, for documents that ARE text — an addendum's body
      lives here so opening it shows the words, not placeholder artwork. */
  note?: string;
}

/** A shape drawn on a drawing sheet. Coordinates are normalised 0..1 against
    the sheet viewBox so they survive any zoom level. */
export interface Markup {
  id: string;
  /** "cloud" — a revision cloud (scalloped outline) around a changed region. */
  kind: "rect" | "pin" | "cloud";
  x: number; y: number;      // rect: top-left · pin: centre
  w?: number; h?: number;    // rect only
  note?: string;
}

export interface Rfi {
  id: string;
  ref: string;           // RFI-003
  bidderId: string;
  subject: string;
  question: string;
  askedAt: string;
  answer?: string;
  answeredAt?: string;
  status: "open" | "answered";
  /** "all" — answer circulated to every bidder; "private" — only the asker. */
  visibility: "all" | "private";
  /** The BIDDER's choice when asking: "public" — happy for the question to be
      seen by all bidders (anonymised); "private" — between them and the
      studio only. Default private for questions asked before this existed. */
  askedVisibility?: "private" | "public";
  /** Set when the question was raised on a drawing sheet. */
  docId?: string;
  markups?: Markup[];
}

export type BidderStatus = "invited" | "registered" | "declined" | "submitted";

export interface Bidder {
  id: string;
  company: string;
  contact: string;
  email: string;
  phone?: string;
  accessCode: string;    // portal login
  invitedAt: string;
  status: BidderStatus;
  lastActive?: string;
}

export type Compliance = "pending" | "compliant" | "non-compliant";
export type BidStatus = "received" | "shortlisted" | "awarded" | "unsuccessful";

export interface Bid {
  id: string;
  bidderId: string;
  amount: number;
  currency: string;
  durationMonths: number;
  submittedAt: string;
  bond: boolean;             // bid bond included
  addendaAck: number;        // addenda acknowledged
  note?: string;
  attachments: string[];     // file names (display only)
  checklist?: BidChecklistItem[];  // the completed submission package
  clarifications?: ClarificationLetter[];  // formal Q&A during evaluation
  compliance: Compliance;
  score?: number;            // evaluation score /100
  status: BidStatus;
}

export type ContractStandard = "FIDIC_REDBOOK_2017" | "FIDIC_REDBOOK_1999" | "AIA_A101_2017";

/** A draft contract pulled from the Contract Generator (read-only here —
    revisions happen in the Contract Generator, then re-import). */
export interface DraftContract {
  standard: ContractStandard;
  standardLabel: string;
  employer: string;
  contractor?: string;       // usually "per tender award"
  engineer?: string;
  contractPrice?: { amount: number; currency: string };
  timeForCompletion: string;
  defectsNotification?: string;
  retentionPercent?: number;
  retentionCapPercent?: number;
  performanceSecurityPercent?: number;
  advancePaymentPercent?: number;
  delayDamagesPerDay?: { amount: number; currency: string };
  delayDamagesCapPercent?: number;
  paymentPeriodDays?: number;
  governingLaw?: string;
  disputeResolution?: string;
  worksDescription?: string;
  drawingsCount?: number;
  parts: string[];           // the forms/documents that make up the contract
  /** The actual contract documents, uploaded by the studio — bidders and the
      client download the originals from here. */
  files?: ContractFile[];
  importedAt: string;
  sourceName?: string;       // file the brief came from
}

export interface ContractFile {
  id: string;
  name: string;
  size?: string;
  fileId: string;            // /api/file id — byte-exact original
  uploadedAt: string;
}

/* --- invitation to tender -------------------------------------------------- */
export interface IttSection {
  key: string;             // letter | scope | projectInfo | drawingsInstructions | specifications | bidderRequirements
  title: string;
  body: string;
}

/** How bidders price the works: a BOQ is issued with the tender documents,
    or no BOQ is provided and bidders take off their own quantities. */
export type PricingBasis = "boq" | "drawings";

export interface InvitationToTender {
  sections: IttSection[];
  constructionStart?: string;  // targeted start on site (ISO date)
  pricingBasis?: PricingBasis; // default "drawings" — bidder quantifies
  issuedAt?: string;           // when emailed to bidders
  updatedAt: string;
}

/** A formal clarification letter issued to ONE bidder during evaluation —
    pricing anomalies, missing documents, confirmations. The bidder answers
    from their room; the exchange stays on the bid record. */
export interface ClarificationLetter {
  id: string;
  ref: string;               // CL-01 (per tender)
  subject: string;
  body: string;
  respondBy?: string;        // ISO date
  issuedAt: string;
  response?: string;
  respondedAt?: string;
}

/** One item of the bid submission package, as included with a sealed bid. */
export interface BidChecklistItem {
  key: string;
  label: string;
  fileName: string;
  fileId?: string;           // the actual uploaded document (R2/KV) — downloadable by the manager
  size?: string;
  note?: string;
}

/* --- client-side roles ------------------------------------------------------ */
/** How much of a portal a client-side person sees: the principal everything,
    a commercial advisor the money-adjacent parts, an observer the essentials. */
export type ClientRole = "principal" | "commercial" | "observer";

/** One person on the client's side of a tender, with their own code + role. */
export interface ClientSeat {
  id: string;
  name: string;
  email: string;
  role: ClientRole;
  accessCode: string;
  invitedAt: string;
  status: "invited" | "active";
  lastActive?: string;
}

/** Studio-level access grades — what a studio teammate's code opens.
    Studio Admin is the only grade that spans BOTH modules; Site Admin runs the
    Project Manager; the tender roles open the Bid Manager across EVERY tender,
    with Tender Admin also creating tenders and awarding. */
export type StudioRole = "studio-admin" | "site-admin" | "tender-admin" | "tender-coordinator" | "comms-manager";

/** A studio-level teammate — signs in with their own code at their studio
    role's level. Lives outside any single project/tender (synced as the
    "studio-team" meta aggregate). */
export interface StudioMember {
  id: string;
  name: string;
  email: string;
  role?: StudioRole;         // default "site-admin" (pre-roles members)
  accessCode: string;
  invitedAt: string;
  status: "invited" | "active";
  lastActive?: string;
}

/* --- bid manager team ------------------------------------------------------ */
/** Studio-side helpers on the tender: admins run everything, coordinators do
    the day-to-day (documents, RFIs, alerts, RFQs), viewers observe. */
export type BidTeamRole = "admin" | "coordinator" | "viewer";
export interface BidTeamMember {
  id: string;
  name: string;
  email: string;
  role: BidTeamRole;
  accessCode: string;
  invitedAt: string;
  status: "invited" | "active";
  lastActive?: string;
}

export type AlertAudience = "bidders" | "client" | "manager" | string; // or a bidder id
export type AlertTrigger = "manual" | "invite" | "addendum" | "rfi-answered" | "deadline" | "award" | "results" | "quote" | "clarification";
export type AlertDelivery = "delivered" | "simulated" | "failed";

export interface EmailAlert {
  id: string;
  audience: AlertAudience;
  audienceLabel: string;
  to: string[];              // resolved emails
  subject: string;
  body: string;
  trigger: AlertTrigger;
  sentAt: string;
  delivery?: AlertDelivery;  // real send outcome, patched in once the worker replies
}

/* --- supply quotation requests (RFQs) ------------------------------------- */
/** One line of a quotation package — what the supplier prices. */
export interface QuoteItem {
  id: string;
  description: string;
  qty?: number;
  unit?: string;             // m² / no. / t / lot …
}

/** A supplier's returned price against a package. */
export interface SupplierQuote {
  id: string;
  supplier: string;
  email?: string;
  amount: number;
  currency: string;
  leadWeeks?: number;
  note?: string;
  fileId?: string;           // the primary received quotation document (byte-exact); mirrors files[0] for back-compat
  fileName?: string;
  files?: { fileId: string; fileName: string }[];  // all documents the supplier attached — quote, schedules, catalogues
  receivedAt: string;
  selected?: boolean;
}

/** A part of the works issued for pricing separately from the main tender —
    e.g. window supply, structural steel, kitchens. */
/** A question on a quotation package — the supply-side mirror of tender RFIs.
    "from-supplier": the studio records a supplier's question and answers it
    (optionally circulating to every issued supplier). "to-supplier": the
    studio asks the supplier to clarify something (usually their quote). */
export interface SupplierQuery {
  id: string;
  ref: string;               // Q-01 (per package)
  supplier: string;
  direction: "from-supplier" | "to-supplier";
  question: string;
  askedAt: string;
  answer?: string;
  answeredAt?: string;
  circulated?: boolean;      // the answer was emailed to every issued supplier
}

export interface QuotePackage {
  id: string;
  ref: string;               // RFQ-01 …
  title: string;
  scope?: string;
  inviteLetter?: string;     // the formal Invitation to Price — becomes the issue email
  items: QuoteItem[];
  docIds?: string[];         // register drawings/specs the suppliers price against
  closeDate?: string;
  issuedAt?: string;
  /** token = the supplier's private submission link (/quote/<token>). */
  issuedTo: { name: string; email: string; token?: string }[];
  quotes: SupplierQuote[];
  queries?: SupplierQuery[]; // clarifications both ways
  status: "draft" | "issued" | "awarded";
}

export interface Activity {
  id: string;
  at: string;
  who: string;
  what: string;
  kind: "docs" | "bidder" | "rfi" | "bid" | "alert" | "contract" | "timeline" | "award";
}

/* --- studio directory ------------------------------------------------------ */
/** One thing that happened with a supplier/contractor — the directory's memory. */
export interface DirectoryEvent {
  role: "rfq-issued" | "quoted" | "quote-won" | "tender-invited" | "bid-submitted" | "tender-awarded";
  ref: string;               // RFQ-02 / TDR-2603
  label: string;             // package or tender title
  at: string;
}

/** A supplier or contractor the studio has worked with — learned automatically
    as RFQs and tenders run; scope tags grow with every engagement. */
export interface DirectoryEntry {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  kind: "supplier" | "contractor";
  tags: string[];            // learned scope keywords (windows, steel, joinery…)
  note?: string;
  history: DirectoryEvent[];
  addedAt: string;
}

/* ============================================================================
   PROJECT MANAGER — day-to-day construction management (Procore-style).
   A Project is the aggregate root for the construction phase: drawing sets
   with revision history, field RFIs, daily logs & targets, the work
   programme (Gantt), material requests, punch list and progress reports.
   ========================================================================== */

export interface DrawingRevision {
  rev: string;
  issuedAt: string;
  by: string;             // who uploaded
  note?: string;
  size?: string;
  filePage?: number;      // page inside the original file (combined-set uploads)
  previewId?: string;     // rendered page image in the sheet store (real preview)
  fileId?: string;        // original file in R2 — enables real download
  fileName?: string;
  pageFileId?: string;    // this sheet's own one-page PDF (split from a combined upload)
  /** Set when this issue is an older revision made current again. */
  restoredFrom?: string;
  /** Fingerprint of the uploaded content (SHA-256 of the file, plus the page
      for a combined set). An upload with the same fingerprint is the same
      file again, and is never issued as a new revision. */
  sha?: string;
}

/** A request to whoever draws the sheet for a new issue — kept on the sheet
    with who asked, who was told, and which revision answered it. */
export interface RevisionRequest {
  id: string;
  at: string;
  by: string;
  to: string[];
  note: string;
  againstRev: string;
  dueOn?: string;
  status: "open" | "resolved";
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedRev?: string;
  /** Whether the email actually went (undefined before the send settles). */
  emailed?: boolean;
}

/** One drawing number with its full revision history — the newest revision is
    current, everything older is superseded automatically. */
export interface DrawingSet {
  id: string;
  number: string;
  title: string;
  discipline: string;
  category: DocCategory;
  revisions: DrawingRevision[];   // oldest → newest; last entry is current
  /** Preliminary sheets are for review/comment (client share); issued sheets
      are formally released for construction. Legacy sheets default to issued. */
  stage?: "preliminary" | "issued";
  issuedAt?: string;
  issuedTo?: string;              // recipient/discipline the sheet was issued to
  /** Saved redline markups on this sheet — persistent annotations that don't
      have to become an RFI. Studio or contractor draw, note and resolve them. */
  redlines?: SheetRedline[];
  /** Withdrawn from use: still on the record and stamped OBSOLETE everywhere
      it appears, out of every download and package, with what replaced it. */
  obsolete?: { at: string; by: string; reason?: string; replacedById?: string };
  /** Off the working register and out of downloads, but kept with every
      revision. Nobody is told — it is housekeeping, not a withdrawal. */
  archived?: { at: string; by: string; reason?: string };
  revisionRequests?: RevisionRequest[];
}

/** A saved redline on a sheet — a group of markups (boxes/pins) drawn on the
    drawing, carrying a note, kept until resolved. */
export interface SheetRedline {
  id: string;
  rev?: string;                   // the revision it was drawn on
  /** "revision-cloud" — clouds found by comparing two issues (see `comparedTo`). */
  kind?: "markup" | "revision-cloud";
  comparedTo?: string;
  markups: Markup[];
  note?: string;
  by: string;
  at: string;
  resolved?: boolean;
  resolvedBy?: string;
  resolvedAt?: string;
}

/** The lifecycle a project moves through — drawings and content nest under the
    active phase. Advance forward only: preliminary → bid → construction →
    handover. Legacy projects default to construction. */
export type ProjectPhase = "preliminary" | "bid" | "construction" | "handover";
export const PROJECT_PHASES: ProjectPhase[] = ["preliminary", "bid", "construction", "handover"];
export const PHASE_LABEL: Record<ProjectPhase, string> = {
  preliminary: "Preliminary", bid: "Bid / Tender", construction: "Construction", handover: "Handover",
};

/** A drawing package shared out on a tokened link — contractor download, a
    subcontractor scoped to their discipline, or a client review set. */
export interface DrawingShare {
  id: string;
  ref: string;                    // PKG-001
  token: string;                  // public share link
  title: string;
  audience: "contractor" | "client" | "subcontractor";
  recipientName?: string;
  recipientEmail?: string;
  stage: "preliminary" | "issued";
  docIds: string[];               // the sheets in the package
  /** View only: the sheets open in the app's viewer but no original can be
      opened or downloaded through the link. */
  noDownload?: boolean;
  disciplines?: string[];         // when scoped to a trade
  note?: string;                  // covering note
  sharedBy: string;
  sharedAt: string;
  viewedAt?: string;
  /** Client review comments against the (preliminary) package. */
  clientNotes?: DrawingShareNote[];
}
export interface DrawingShareNote {
  id: string;
  docNumber?: string;             // which sheet the note is about
  text: string;
  by: string;
  at: string;
  resolved?: boolean;
  resolvedAt?: string;
}

/** Who owes the next move on a record. One vocabulary across every register —
    it is what "My Tasks", overdue escalation and the digests all read. */
export type BallInCourt = "manager" | "contractor" | "consultant" | "client" | "supplier";

export const BALL_LABEL: Record<BallInCourt, string> = {
  manager: "Studio", contractor: "Contractor", consultant: "Consultant",
  client: "Client", supplier: "Supplier",
};

/** Accountability, carried by every workflow record: one party owes the next
    move, one person's name is on it, one date it is owed by. */
export interface Accountable {
  ballInCourt?: BallInCourt;
  assignee?: string;         // person or company carrying it
  dueDate?: string;          // ISO date
}

/** A comment on any record — discussion stays attached to the thing it is
    about rather than drifting into email. */
export interface RecordComment {
  id: string;
  by: string;
  company?: string;
  at: string;
  text: string;
  mentions?: string[];       // names @-mentioned; each is notified
  translated?: boolean;
  originalText?: string;
}

/** A record dropped at a point on a drawing. Coordinates are normalised 0..1
    against the sheet viewBox, exactly like Markup, so pins survive any zoom. */
export interface RecordPin {
  docId: string;             // DrawingSet id
  x: number;
  y: number;
  sheet?: string;            // sheet number at pin time — display without a lookup
}

/** Anything that can be discussed, located and owed by someone. */
export interface Trackable extends Accountable {
  comments?: RecordComment[];
  pin?: RecordPin;
}

/** A file attached to an RFI — a supporting photo/PDF on the question, or a
    sketch / marked-up detail issued with the answer. */
export interface RfiAttachment {
  id: string;
  /** R2 object. Absent for a sheet reference, or an image whose upload
      failed but whose inline thumbnail still made it onto the record. */
  fileId?: string;
  name: string;
  size?: string;
  by: string;
  at: string;
  role: "question" | "answer";
  /** What it is — a photo from site, a document, or a sheet in the register. */
  kind?: "image" | "file" | "sheet";
  /** Images carry a small inline copy so the chip shows the picture on site. */
  thumb?: string;
  /** kind "sheet": the drawing in the register this RFI is about. */
  docId?: string;
  sheetNumber?: string;
}

/** A drawing or image issued with ONE line of an RFI answer. A sketch that
    answers point 3 belongs to point 3 — pooling it against the whole RFI makes
    the contractor guess which line it settles. */
export interface RfiPointFile {
  id: string;
  /** R2 object — a PDF, a photo, a marked-up detail. */
  fileId?: string;
  name: string;
  size?: string;
  /** Images carry a small inline copy so the thumbnail renders offline/on site. */
  thumb?: string;
  kind: "image" | "file" | "sheet";
  /** kind "sheet": a sheet already in the register, optionally marked up. */
  docId?: string;
  sheetNumber?: string;
  markups?: Markup[];
  by: string;
  at: string;
}

/** One line-by-line answer to a numbered RFI point. Stored in both languages
    so neither side sees a mix. */
export interface RfiPointAnswer {
  key: string;             // "sectionIndex:pointIndex" in the parsed outline
  en: string;
  zh?: string;
  by: string;
  at: string;
  /** Drawings and images issued with THIS line of the answer. */
  files?: RfiPointFile[];
  /** The contractor's response to THIS point's answer — accept it, or query it
      (send it back to the studio with a note). */
  ack?: "accepted" | "queried";
  ackNote?: string;
  ackBy?: string;
  ackAt?: string;
}

/* ---- construction bottlenecks --------------------------------------------
   The thing actually holding the job up — information the contractor needs, a
   quotation awaiting approval, a decision nobody has made — tracked as its own
   record because one bottleneck often blocks a dozen programme tasks at once.
   Open/closed, ball-in-court, a live comment thread, named stakeholders who
   are emailed on every development, and cron reminders that keep nudging until
   it is closed. */
export type BottleneckKind = "information" | "approval" | "decision" | "material" | "payment" | "access" | "other";

export const BOTTLENECK_KIND_LABEL: Record<BottleneckKind, string> = {
  information: "Information needed", approval: "Approval needed", decision: "Decision needed",
  material: "Material / supply", payment: "Payment", access: "Site access", other: "Other",
};

export type BottleneckImpact = "medium" | "high" | "critical";

export interface BottleneckComment {
  id: string;
  by: string;
  at: string;
  body: string;
}

export interface BottleneckStakeholder {
  name: string;
  email: string;
  role?: string;             // "Client", "Contractor", "MEP consultant"…
}

/* ---------------------------------------------------------------------------
   LONG-LEAD ITEMS — the materials and equipment that take longer to arrive
   than the programme can absorb: steel, lifts, switchgear, windows, stone.
   Each is tied to the activities that cannot start without it, so its
   order-by date falls out of the programme and moves when the programme moves. */
export type LongLeadStatus = "identified" | "quoting" | "ordered" | "in-transit" | "on-site" | "cancelled";
export const LONG_LEAD_LABEL: Record<LongLeadStatus, string> = {
  identified: "Identified", quoting: "Quoting", ordered: "Ordered", "in-transit": "In transit", "on-site": "On site", cancelled: "Cancelled",
};
export interface LongLeadItem {
  id: string;
  ref: string;                 // LL-001
  name: string;
  category?: string;           // Structure, MEP, Envelope, Finishes, Plant…
  spec?: string;
  qty?: string;
  leadDays: number;            // order placed → on site
  bufferDays?: number;         // safety before the first activity needs it (default 14)
  taskIds: string[];           // programme activities that cannot start without it
  requiredOn?: string;         // manual need date when no activity is linked
  status: LongLeadStatus;
  supplier?: string;
  poRef?: string;
  orderedOn?: string;
  eta?: string;
  onSiteOn?: string;
  note?: string;
  rationale?: string;          // why the probe flagged it
  source: "ai" | "manual";
  materialId?: string;         // the MR it became, if one was raised
  calledForwardOn?: string;    // last call-forward sent to the contractor / supplier
  remindedOn?: string;         // last automatic reminder
  createdAt: string;
  updatedAt: string;
  by: string;
}

export interface Bottleneck {
  id: string;
  ref: string;               // BN-001
  title: string;             // "Kitchen quotation awaiting client approval"
  detail?: string;           // what exactly is needed, and from whom
  kind: BottleneckKind;
  impact: BottleneckImpact;
  status: "open" | "closed";
  ballInCourt: BallInCourt;
  raisedBy: string;
  raisedAt: string;
  dueBy?: string;            // when it must clear to hold the programme
  /** Programme activities this is holding up — the cost of leaving it open. */
  blockedTaskIds?: string[];
  /** Everyone who must hear about every development, and gets the reminders. */
  stakeholders: BottleneckStakeholder[];
  comments: BottleneckComment[];
  closedBy?: string;
  closedAt?: string;
  resolution?: string;
  /** Stamped by the cron sweep so reminders pace themselves. */
  remindedOn?: string;
}

export type RfiPriority = "routine" | "urgent" | "critical";
export const RFI_PRIORITY_LABEL: Record<RfiPriority, string> = { routine: "Routine", urgent: "Urgent", critical: "Critical — work stopped" };

export interface PmRfi extends Trackable {
  id: string;
  ref: string;
  subject: string;
  question: string;
  askedBy: string;         // display name/company
  ballInCourt: BallInCourt;
  /** Submitted — the full timestamp, so turnaround is measured, not guessed. */
  askedAt: string;
  /** The date the contractor needs the answer by. Asked for on every RFI. */
  dueDate?: string;
  /** How hard the missing answer bites, as the contractor states it. */
  priority?: RfiPriority;
  /** What the answer holds up — the pour, the order, the inspection. */
  reason?: string;
  answer?: string;          // studio answer, canonical (English)
  answerZh?: string;        // the answer rendered into Chinese for the contractor
  answeredAt?: string;
  status: "open" | "answered";
  docId?: string;          // DrawingSet id
  markups?: Markup[];
  /** Per-point answers for a numbered RFI — the studio replies line by line.
      Keyed by the point's position in the parsed outline ("si:pi"). */
  pointAnswers?: RfiPointAnswer[];
  /** Files attached to the RFI — question supporting docs and answer sketches. */
  attachments?: RfiAttachment[];
  /** Linked programme activity — the schedule task this RFI / request is for. */
  taskId?: string;
  /** What kind of request: a clarification, a drawing to be issued, or other
      information needed before a scheduled activity can proceed. */
  rfiKind?: "clarification" | "drawing" | "information";
  /** Set when the contractor wrote it in Chinese and AI translated it. */
  translated?: boolean;
  originalText?: string;         // legacy: subject+question original, joined
  originalSubject?: string;      // the subject as the contractor wrote it (Chinese)
  originalQuestion?: string;     // the question as the contractor wrote it (Chinese)
  /** The contractor confirms they've read and accept the studio's answer. */
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  /** Mind Map RFI-board node id, once a contractor RFI has been pushed there. */
  mindmapNodeId?: string;
}

/** An ad-hoc to-do anyone on the project can add — the contractor's task list. */
export interface AdhocTask {
  id: string;
  title: string;
  by: string;
  assignee?: string;
  due?: string;
  done: boolean;
  createdAt: string;
  doneAt?: string;
}

export type Weather = "sunny" | "cloudy" | "rain" | "storm";

export interface CrewLine { trade: string; count: number; }

/** One person on site, named. Counting heads answers "how many"; a contract
    argument needs "who, doing what, under whose supervision, certified how". */
export interface CrewMember {
  id: string;
  name: string;
  trade: string;
  /** Foreman, operative, plant operator, banksman… */
  role?: string;
  /** National ID or site badge number — who was actually on site that day. */
  idNo?: string;
  phone?: string;
  /** Trade certificate, plant licence, scaffold ticket, induction date. */
  certification?: string;
  hours?: number;
  /** Sub-contractor or agency this person came through. */
  employer?: string;
  note?: string;
}

/** A part of the site being worked, and what state it is in. Locations on
    activity lines say where a task happened; this says what the area is now. */
export interface WorkArea {
  id: string;
  name: string;              // "Block B — units 8–10"
  detail?: string;           // what is happening there
  trades?: string[];
  /** Where it stands: "ring beam complete, ready for pour". */
  status?: string;
  /** Sheet numbers governing this area — links the report to the drawings. */
  sheetRefs?: string[];
  /** Photographs of THIS area on the day. An area described but not shown is
      a claim; an area photographed is a record. */
  photoIds?: string[];
}

/** A fixed camera position on the site — "the main view from the north-east".

    The value of a progress photo is comparability: the same frame, day after
    day, is a time-lapse of the building going up and settles an argument about
    what was standing when. Left to choose freely, a crew photographs whatever
    they were working on, and no two days line up. So the studio fixes the
    vantage points once and the daily report asks for each of them by name,
    showing the previous day's frame to line the shot up against. */
export interface SiteViewpoint {
  id: string;
  /** "North elevation from the gate" — what the photographer should stand at. */
  name: string;
  /** Optional framing note: "include the full gable and the scaffold line". */
  guide?: string;
  /** Set false to retire a vantage point without losing its history. */
  active?: boolean;
}

/** What the daily report insists on before it can be filed.

    A report is only worth reading if the fields that carry meaning are filled,
    and on a busy site the only way that happens is if the form asks properly.
    Every rule is off unless the studio turns it on, so no existing project
    suddenly refuses a report. */
export interface DailyReportRules {
  /** At least one trade with a head count. */
  crew?: boolean;
  /** At least one activity line. */
  activities?: boolean;
  /** At least one work area, each named with its status. */
  workAreas?: boolean;
  /** Every work area must carry at least one photograph. */
  areaPhotos?: boolean;
  /** Every active viewpoint must be photographed. */
  viewpointPhotos?: boolean;
  /** At least one commitment for tomorrow. */
  nextTargets?: boolean;
  /** Named personnel, not just trade counts. */
  roster?: boolean;
  /** Plant and equipment on site. */
  equipment?: boolean;
}

/** How hard a challenge is biting. A delay has already cost time; a challenge
    may still be headed off, which is the whole reason to report it early. */
export type ChallengeSeverity = "watch" | "impacting" | "stopped";

export const CHALLENGE_LABEL: Record<ChallengeSeverity, string> = {
  watch: "Watching", impacting: "Slowing work", stopped: "Work stopped",
};

/** Something standing in the contractor's way. */
export interface SiteChallenge {
  id: string;
  text: string;
  severity: ChallengeSeverity;
  area?: string;
  /** What the contractor has done or proposes to do about it. */
  action?: string;
  /** Trades or activities held up by it. */
  affects?: string;
}

/** Who the contractor is waiting on. */
export type InfoSource = "studio" | "client" | "consultant" | "supplier";

export const INFO_SOURCE_LABEL: Record<InfoSource, string> = {
  studio: "Studio", client: "Client", consultant: "Consultant", supplier: "Supplier",
};

/** Something the contractor needs in order to keep going. Lighter than a
    formal RFI — most of these are answered in a sentence — but it is recorded
    and dated so "we asked weeks ago" is a fact rather than a claim. Any one of
    them can be promoted into a real RFI when it needs the formal route. */
export interface InfoRequest {
  id: string;
  text: string;
  from: InfoSource;
  /** The date after which this starts costing time. */
  neededBy?: string;
  /** What stops or slows without it. */
  blocking?: string;
  answered?: string;
  answeredAt?: string;
  answeredBy?: string;
  /** Set once escalated — the RFI then carries it. */
  rfiRef?: string;
}

/** One structured line of site work — daily reports and visit reports both
    use it: what happened, which trade, where, with its own photos. */
export interface ActivityLine {
  id: string;
  text: string;
  trade?: string;
  location?: string;       // block / level / chainage
  photoIds?: string[];
  /** Quantity placed against the measure — 42 of 120 m². */
  qty?: number;
  unit?: string;
  /** Drawing sheets and BOQ items this work is measured against. Filled by
      hand or by the AI expansion, which cites what it was given. */
  sheetRefs?: string[];
  boqRefs?: string[];
  /** Set when the line was expanded from a short note by the AI drafter, with
      what was originally typed, so nobody loses their own words. */
  expanded?: boolean;
  originalText?: string;
}

/** An instruction the site received — stated by the contractor, with the paper
    photographed where there was any. `ref` is filled in once it is written to
    the correspondence register, so the report and the register agree. */
export interface SiteInstructionReceived {
  id: string;
  /** What was instructed, in the words it was given in. */
  text: string;
  /** Who gave it — a name, not a company: an instruction is given by a person. */
  from: string;
  /** How it arrived. Verbal instructions are the ones that cause disputes. */
  how: "verbal" | "written" | "drawing" | "email";
  /** Where on site, when it matters. */
  location?: string;
  /** The contractor's view that this changes cost or time — a flag, not a claim. */
  affectsCost?: boolean;
  affectsTime?: boolean;
  /** Photograph of the instruction book page, marked-up drawing or letter. */
  fileIds?: { id: string; name: string }[];
  /** The register ref this became — SI-007. */
  ref?: string;
  correspondenceId?: string;
}

/* ===========================================================================
   WEEKLY GOALS — set by the studio, handed to the contractor.

   A target is what the contractor commits to; a goal is what the studio asks
   for. They are deliberately separate records: conflating them lets a missed
   ask be quietly rewritten as a target that was never set, and the week ends
   with nothing to answer for. A goal is issued, the contractor responds to it,
   and at the end of the week somebody says plainly whether it was reached.
   ========================================================================= */
export type GoalStatus = "issued" | "accepted" | "queried" | "met" | "partly-met" | "missed";

export const GOAL_STATUS_LABEL: Record<GoalStatus, string> = {
  issued: "Issued", accepted: "Accepted", queried: "Queried",
  met: "Met", "partly-met": "Partly met", missed: "Missed",
};

export interface WeeklyGoal {
  id: string;
  /** Monday of the week it belongs to. */
  week: string;
  title: string;
  /** What "reached" means — the studio's own definition, so the assessment at
      the end of the week is not an argument about wording. */
  measure?: string;
  detail?: string;
  priority: "must" | "should";
  setBy: string;
  setAt: string;
  status: GoalStatus;
  /** The contractor's answer when the goal is handed over. */
  response?: string;
  respondedBy?: string;
  respondedAt?: string;
  /** The end-of-week verdict, and who gave it. */
  outcome?: string;
  assessedBy?: string;
  assessedAt?: string;
  /** Contractor targets raised against this goal, so accomplishment can be read
      off the targets rather than asserted. */
  targetIds?: string[];
  /** Visible in the client portal and printed in the client's weekly report. */
  sharedWithClient?: boolean;
  translated?: boolean;
  originalText?: string;
}

export interface DailyLog {
  id: string;
  date: string;            // ISO date
  weather: Weather;
  tempC?: number;
  crew: CrewLine[];
  equipment?: string;
  workDone: string[];      // flat lines — derived from `activities` on new reports
  activities?: ActivityLine[];   // structured log with trade/location/photos
  teamPresent?: string[];        // team members on site that day
  observationRefs?: string[];    // OBS-refs raised with this report
  delays?: string;
  visitors?: string;
  note?: string;
  /** The person who filed it, and the firm they filed it for. `by` used to
      hold the company — a report signed by an organisation tells you nobody
      is answerable for it, so it now names the individual and `company`
      carries the firm. Older rows keep the company in `by` and no `company`. */
  by: string;
  company?: string;
  photoIds?: string[];     // SitePhoto ids attached to this report
  /** Who was actually on site, named — the contractor's own record. */
  roster?: CrewMember[];
  /** The parts of the site worked, and what state each is in. */
  areas?: WorkArea[];
  /** What the contractor commits to tomorrow. Carried into the next day's
      report so yesterday's plan can be read against what happened. */
  nextTargets?: string[];
  /** What is standing in the way, and what is needed to clear it. Both are
      escalated to the top of the report — they are why it is read today
      rather than filed. */
  challenges?: SiteChallenge[];
  infoNeeded?: InfoRequest[];
  /** Instructions given on site that day — most often verbally, by whoever was
      standing there. Captured on the report because that is the only moment
      the contractor is certain to be asked, and each one is also written into
      the correspondence register so it stops being hearsay. */
  instructions?: SiteInstructionReceived[];
  acknowledged?: { by: string; at: string };   // manager sign-off
  /** Set once this report has been emailed to the client — by the morning
      auto-push or the manager's share button. Stops the morning sweep from
      sending the same day twice. */
  clientEmailed?: { at: string; to?: number };
  /** Set when the contractor wrote it in Chinese and AI translated it. */
  translated?: boolean;
  originalText?: string;
}

/** A compressed site photo — synced to D1 as its own row so galleries stay
    within aggregate size limits. */
export interface SitePhoto {
  id: string;
  /** Pixel size of the original held in R2 under `fileId`. Present only on
      photographs captured since full-quality storage came in — its absence
      means the row's own thumbnail IS the best copy there is. */
  fullWidth?: number;
  fullHeight?: number;
  projectId: string;
  date: string;            // day the photo belongs to
  caption?: string;
  activity?: string;       // tag: "Blockwork", "Drainage"…
  by: string;
  takenAt: string;
  dataUri: string;         // jpeg (or svg for seeded placeholders)
  /** "render" rows are design visuals, kept out of the site gallery.
      "works" and "workforce" split the daily report's photo sections —
      what was built, and who built it. Plain "site" reads as works. */
  category?: "site" | "render" | "works" | "workforce";
  /** Drawing sets (by id) that depict the space/element in this photo — the
      contractor opens them straight from the image. Set by hand or by the AI
      association pass. */
  linkedDocIds?: string[];
  /** true when the links were suggested by the AI vision pass (vs set by hand). */
  linksAi?: boolean;
  /** Highlighted on the curated progress timeline (studio-picked milestones). */
  featured?: boolean;
  /** Full-resolution original in R2 (panoramas / 360 VR) — the synced row keeps
      only a light thumbnail in `dataUri`; the viewer loads this for full quality. */
  fileId?: string;
  /** A site video is the same record with a moving original: `dataUri` holds a
      still grabbed from the first frame so every gallery, report and PDF can
      lay it out exactly like a photograph, and `fileId` holds the video itself
      in R2 for playback. A PDF prints the still and marks it as film. */
  media?: "photo" | "video";
  /** Video only — runtime in seconds, and the file's own type for <source>. */
  durationSec?: number;
  mimeType?: string;
  /** Taken at one of the project's fixed vantage points — this is what makes a
      day-by-day progression series possible. */
  viewpointId?: string;
  /** The work area this shot belongs to, when it was taken for one. */
  areaId?: string;
}

export const PHOTO_CATEGORY_LABEL: Record<string, string> = {
  works: "Works completed", workforce: "Workforce", site: "Site", render: "Render",
};

export type ObservationCategory = "safety" | "quality" | "progress" | "design" | "general";

/** Field observation raised by any team member; the studio responds/closes. */
export interface Observation extends Trackable {
  id: string;
  ref: string;             // OBS-007
  by: string;
  company: string;
  category: ObservationCategory;
  note: string;
  location?: string;
  photoId?: string;
  at: string;
  status: "open" | "closed";
  response?: string;
  respondedAt?: string;
  /** Set when the contractor wrote it in Chinese and AI translated it. */
  translated?: boolean;
  originalText?: string;
}

export interface SiteVisitDirective {
  id: string;
  text: string;
  assignee: string;
  due?: string;
  done: boolean;
}

/** Studio/PM site visit report — observations and directives from a walk. */
export interface SiteVisit {
  id: string;
  ref: string;             // SV-003
  date: string;
  attendees: string;       // display string — derived from `teamPresent` on new visits
  purpose: string;
  weather?: Weather;
  tempC?: number;
  teamPresent?: string[];        // structured attendee list
  activities?: ActivityLine[];   // works inspected, with photos
  observationRefs?: string[];    // OBS-refs raised on the walk
  notes: string[];         // observations & reportings
  directives: SiteVisitDirective[];
  photoIds: string[];
  by: string;
  issuedAt: string;
}

/** A date the project is being driven toward — structural top-out, client
    handover, practical completion. The dashboard counts down to the next one. */
export interface DateProbe {
  id: string;
  date: string;             // the day it fires
  title: string;
  detail?: string;
  /** The milestone this probe guards, when it was made from one. */
  milestoneId?: string;
  setBy: string;
  /** Stamped by the sweep (or a manual send) — a probe fires once. */
  sentAt?: string;
}

export interface ProjectMilestone {
  id: string;
  label: string;
  date: string;            // ISO date
  note?: string;
}

export interface GanttTask {
  id: string;
  name: string;
  phase: string;
  start: string;           // ISO date
  end: string;
  progress: number;        // 0–100
  owner: string;           // contractor / trade
  baselineStart?: string;
  baselineEnd?: string;
  milestone?: boolean;
  /** On the critical path — slipping this slips the completion date. */
  critical?: boolean;
  /** Days of float before this activity starts driving the end date. */
  floatDays?: number;
  /** Activities that must finish before this one starts. */
  dependsOn?: string[];
  /** Work is blocked and cannot proceed until the reason is cleared. */
  blocked?: boolean;
  blockReason?: string;
  blockedAt?: string;
  /** Comments, queries, snags and risk / block-point flags against the task. */
  notes?: TaskNote[];
  /** What this row IS. A contractor's programme carries lines like "rebar on
      site by 12 Oct" — a date materials are required by, not work. Those are
      "procurement" rows: shown on the timeline as a need date, kept out of
      progress, planned %, behind and critical-path maths, and analysed for
      the activities they feed. Absent = an activity. */
  kind?: "activity" | "procurement";
  /** When work actually began / finished on site, against the planned
      start / end. Filled by hand, or on the day progress first moves. */
  actualStart?: string;
  actualFinish?: string;
  /** Set when the planned dates were moved by hand after the baseline was
      fixed — a rescheduling, not a correction. The baseline keeps the
      original so the slip stays visible. */
  adjusted?: { from: string; to: string; at: string; by: string };
  /** Procurement rows only: the activities that cannot start without it. */
  feeds?: string[];
  procurement?: {
    status: "to-order" | "ordered" | "delivered";
    supplier?: string;
    orderedOn?: string;
    eta?: string;
    deliveredOn?: string;
    longLeadId?: string;
    materialId?: string;
  };
}

/** One line of a subcontractor's own programme — their sequence, run beside
    the main programme. It may follow a main activity (it cannot start until
    that finishes) and it carries its own actuals. */
export interface SubTask {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
  /** A main-programme activity this line waits on. */
  afterTaskId?: string;
  actualStart?: string;
  actualFinish?: string;
  note?: string;
}

export type TaskNoteKind = "comment" | "query" | "snag" | "risk";

/** A note on a programme activity (or the programme as a whole). Queries, snags
    and risks are trackable — they open and close; comments are just discussion.
    A "risk" is a potential block-point raised before it stops work. */
export interface TaskNote {
  id: string;
  kind: TaskNoteKind;
  text: string;
  by: string;
  at: string;              // ISO datetime
  resolved?: boolean;      // query / snag / risk closed
  resolvedBy?: string;
  resolvedAt?: string;
  /** Who must act on this — the query shows on that side's dashboard. */
  ball?: "studio" | "contractor";
  translated?: boolean;    // machine-translated from Chinese
  originalText?: string;   // the original 中文 when translated
}

/** A commitment for a day or a week. Weekly targets carry the Monday as their
    date and are reviewed at the end of the week; daily ones roll over. */
export type TargetScope = "day" | "week";

export interface DailyTarget {
  id: string;
  date: string;            // ISO date — the day, or the Monday for a weekly target
  text: string;
  trade?: string;
  done: boolean;
  carried?: boolean;       // carried over from a previous day
  scope?: TargetScope;     // undefined = "day" (every existing target)
  /** Optional measurable: 40 of 120 m² poured. */
  qty?: number;
  qtyDone?: number;
  unit?: string;
  owner?: string;
  /** Linked programme activity — the target commits progress on this task. */
  taskId?: string;
}

export type MaterialStatus = "requested" | "under-review" | "approved" | "ordered" | "in-transit" | "delivered" | "rejected";

/** Link into the Task Mind Map procurement subsystem. */
export interface MindmapLink {
  materialId: string;
  taskNodeId?: string;
  status?: string;         // mind-map pipeline status from the last probe
  latestNote?: string;
  lastProbedAt?: string;
  pushedAt: string;
}

export interface MaterialRequest extends Trackable {
  id: string;
  ref: string;             // MR-004
  item: string;
  spec?: string;
  /** Where on site it's going — block, level, chainage. On the request form
      so the studio and the supplier both know what the delivery is FOR. */
  area?: string;
  qty: number;
  unit: string;
  neededBy: string;
  status: MaterialStatus;
  /** Linked programme activity — procurement against a scheduled task, so the
      look-ahead can warn when a material won't arrive before the work starts. */
  taskId?: string;
  leadDays?: number;       // procurement lead time in days, for readiness maths
  requestedBy: string;
  requestedAt: string;
  supplier?: string;
  /** Quoted abroad — the converted figure and the rate used are kept. */
  foreign?: ForeignPrice;
  note?: string;
  updatedAt?: string;
  mindmap?: MindmapLink;
  /** Set by the contractor when the material ships — carrier, tracking and an
      ETA — so the studio can see what's in transit and awaiting delivery. */
  shipment?: { at: string; by: string; carrier?: string; tracking?: string; eta?: string };
  /** Set when the contractor wrote it in Chinese and AI translated it. */
  translated?: boolean;
  originalText?: string;
}

export type PunchStatus = "open" | "ready-for-review" | "closed";

export interface PunchItem extends Trackable {
  id: string;
  ref: string;             // PL-012
  title: string;
  location: string;
  trade: string;
  assignee: string;
  status: PunchStatus;
  raisedAt: string;
  due?: string;
  /** Set when the item came out of a failed inspection line. */
  fromInspection?: string;
  /** Set when the item came out of a failed MEP test. */
  fromMep?: string;
}

/* ===========================================================================
   MEP & SERVICES — the building-services register. Each mechanical,
   electrical, plumbing, fire or ELV system is tracked from first fix through
   testing to commissioning, and its test records live on the system itself,
   so the T&C evidence sits where handover will come looking for it.
   ======================================================================== */

export type MepDiscipline = "Mechanical" | "Electrical" | "Plumbing" | "Fire" | "ELV";

/** The install lifecycle, in order. A system moves along it one way; only the
    studio signs the last step. */
export const MEP_STAGES = ["not-started", "first-fix", "second-fix", "testing", "commissioned"] as const;
export type MepStage = (typeof MEP_STAGES)[number];

export type MepTestResult = "pending" | "passed" | "failed";

/** One test or commissioning check — pressure test, megger, flush, balance —
    with who recorded the result and when. */
export interface MepTest {
  id: string;
  name: string;
  result: MepTestResult;
  at?: string;
  by?: string;
  note?: string;
  /** Punch ref raised from a failed test. */
  raisedRef?: string;
}

export interface MepSystem extends Trackable {
  id: string;
  ref: string;             // MEP-004
  discipline: MepDiscipline;
  name: string;            // "Chilled-water riser CHW-1"
  zone: string;            // where it runs / what it serves
  contractor?: string;     // installing firm
  stage: MepStage;
  tests: MepTest[];
  note?: string;
  createdAt: string;
  updatedAt?: string;
  /** Studio sign-off, stamped when the stage reaches commissioned. */
  commissionedBy?: string;
  commissionedAt?: string;
}

/* ===========================================================================
   SUBMITTALS — the technical approval register. A contractor submits shop
   drawings, samples, method statements and certificates; the studio reviews
   and responds. Each resubmission is a new revision on the same record, so
   the whole approval history reads in one place.
   ========================================================================= */
export type SubmittalKind =
  | "shop-drawing" | "material-sample" | "method-statement" | "mix-design"
  | "mock-up" | "certificate" | "calculation" | "as-built" | "other";

export const SUBMITTAL_KIND_LABEL: Record<SubmittalKind, string> = {
  "shop-drawing": "Shop drawing", "material-sample": "Material / sample",
  "method-statement": "Method statement", "mix-design": "Mix design",
  "mock-up": "Mock-up", certificate: "Certificate", calculation: "Calculation",
  "as-built": "As-built", other: "Other",
};

/** The reviewer's verdict — a fixed vocabulary, because "OK" in an email is
    not a contractual answer. */
export type SubmittalResponse = "approved" | "approved-as-noted" | "revise-resubmit" | "rejected" | "for-record";

export const SUBMITTAL_RESPONSE_LABEL: Record<SubmittalResponse, string> = {
  approved: "Approved", "approved-as-noted": "Approved as noted",
  "revise-resubmit": "Revise & resubmit", rejected: "Rejected", "for-record": "Received for record",
};

export interface SubmittalRevision {
  rev: string;              // 01, 02 …
  submittedAt: string;
  submittedBy: string;
  note?: string;
  fileId?: string;
  fileName?: string;
  size?: string;
  response?: SubmittalResponse;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;      // the written response that goes back
}

export type SubmittalStatus = "draft" | "submitted" | "under-review" | "approved" | "rejected" | "closed";

export interface Submittal extends Trackable {
  id: string;
  ref: string;              // SUB-014
  title: string;
  kind: SubmittalKind;
  specSection?: string;     // "03 30 00 — Cast-in-place concrete"
  discipline?: string;
  packageRef?: string;      // groups related submittals for one package
  requiredOnSite?: string;  // the date the material is needed — drives the review deadline
  leadTimeWeeks?: number;
  revisions: SubmittalRevision[];
  status: SubmittalStatus;
  raisedAt: string;
  raisedBy: string;
  closedAt?: string;
  translated?: boolean;
  originalText?: string;
}

/* ===========================================================================
   CHANGE EVENTS → VARIATIONS. Something happens (an RFI answer, an
   instruction, a site condition); it is captured as an event with an early
   cost and time view, priced, then approved into a formal variation that
   moves the contract sum. One record carries the whole chain.
   ========================================================================= */
export type ChangeOrigin =
  | "rfi" | "instruction" | "design-change" | "site-condition"
  | "client-request" | "statutory" | "provisional-sum" | "other";

export const CHANGE_ORIGIN_LABEL: Record<ChangeOrigin, string> = {
  rfi: "RFI answer", instruction: "Instruction", "design-change": "Design change",
  "site-condition": "Site condition", "client-request": "Client request",
  statutory: "Statutory requirement", "provisional-sum": "Provisional sum", other: "Other",
};

/** event → quoting → submitted → approved / rejected. Only "approved" moves
    the contract sum. */
export type VariationStatus = "event" | "quoting" | "submitted" | "approved" | "rejected" | "withdrawn";

export const VARIATION_STATUS_LABEL: Record<VariationStatus, string> = {
  event: "Change event", quoting: "Awaiting price", submitted: "Submitted for approval",
  approved: "Approved", rejected: "Rejected", withdrawn: "Withdrawn",
};

export interface Variation extends Trackable {
  id: string;
  ref: string;              // CE-007
  variationNo?: string;     // VO-003 — issued only on approval
  title: string;
  description: string;
  origin: ChangeOrigin;
  originRef?: string;       // RFI-014 / SI-006
  status: VariationStatus;
  estimatedCost?: number;   // the studio's early view
  quotedCost?: number;      // the contractor's price
  approvedCost?: number;    // what was agreed — this is what moves the sum
  timeImpactDays?: number;
  raisedAt: string;
  raisedBy: string;
  quotedAt?: string;
  decidedAt?: string;
  decidedBy?: string;
  decisionNote?: string;
  fileIds?: { id: string; name: string }[];
}

/* ===========================================================================
   CONTRACT CORRESPONDENCE — the formal paper trail: instructions, notices,
   claims and early warnings, each numbered, issued, acknowledged and answered.
   ========================================================================= */
export type CorrespondenceKind =
  | "site-instruction" | "architect-instruction" | "notice" | "eot-claim"
  | "early-warning" | "letter" | "transmittal" | "email";

export const CORRESPONDENCE_LABEL: Record<CorrespondenceKind, string> = {
  "site-instruction": "Site instruction", "architect-instruction": "Architect's instruction",
  notice: "Notice", "eot-claim": "Extension of time claim",
  "early-warning": "Early warning", letter: "Letter", transmittal: "Transmittal", email: "Email",
};

/** Ref prefixes per kind, so the register numbers itself correctly. */
export const CORRESPONDENCE_PREFIX: Record<CorrespondenceKind, string> = {
  "site-instruction": "SI", "architect-instruction": "AI", notice: "NOT",
  "eot-claim": "EOT", "early-warning": "EW", letter: "LTR", transmittal: "TRN", email: "COR",
};

export type CorrespondenceStatus = "issued" | "acknowledged" | "responded" | "closed";

export interface Correspondence extends Trackable {
  id: string;
  ref: string;              // SI-004
  kind: CorrespondenceKind;
  subject: string;
  body: string;
  issuedBy: string;
  issuedTo: string;
  issuedAt: string;
  requiresResponseBy?: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  response?: string;
  respondedAt?: string;
  status: CorrespondenceStatus;
  /** Flags that this instruction carries cost — a change event should follow. */
  costImpact?: boolean;
  variationId?: string;     // the change event raised from it
  fileIds?: { id: string; name: string; size?: string }[];
  translated?: boolean;
  originalText?: string;
  /** Email recipients, when it went out as mail from the interface. */
  to?: string[];
  cc?: string[];
  /** Every send attempt — who it went to, when, and whether the mail service took it. */
  deliveries?: CorrespondenceDelivery[];
  /** The record this one replies to or forwards. */
  inReplyTo?: string;
  /** "out" — issued by the studio; "in" — received and recorded. */
  direction?: "out" | "in";
}

export interface CorrespondenceDelivery {
  at: string;
  by: string;
  to: string[];
  cc?: string[];
  ok: boolean;
  attachments?: number;
  note?: string;
}

/* ===========================================================================
   INSPECTIONS & CHECKLIST TEMPLATES — a reusable checklist executed on site
   line by line, with a photo and a note where it matters, and a failure that
   becomes a punch item rather than a forgotten remark.
   ========================================================================= */
export type CheckResult = "pass" | "fail" | "na";
export type ChecklistCategory = "quality" | "safety" | "commissioning" | "handover" | "pre-pour" | "other";

export const CHECKLIST_CATEGORY_LABEL: Record<ChecklistCategory, string> = {
  quality: "Quality", safety: "Safety", commissioning: "Commissioning",
  handover: "Handover", "pre-pour": "Pre-pour", other: "Other",
};

export interface ChecklistLine {
  id: string;
  text: string;
  guidance?: string;        // what "pass" actually means
  photoRequired?: boolean;
}

export interface ChecklistSection {
  id: string;
  title: string;
  lines: ChecklistLine[];
}

export interface ChecklistTemplate {
  id: string;
  ref: string;              // CHK-03
  name: string;             // "Pre-pour — slab on grade"
  category: ChecklistCategory;
  intro?: string;           // what this inspection is for / when to run it
  sections: ChecklistSection[];
  updatedAt: string;
  updatedBy?: string;
}

export interface InspectionLine {
  lineId: string;
  result?: CheckResult;
  note?: string;
  photoIds?: string[];
  /** Punch ref raised from a failed line. */
  raisedRef?: string;
}

/** The life of a site inspection: the contractor asks for one, the studio
    books it, the contractor says the work is ready, the inspector runs the
    checklist in place, and the sign-off either releases the next stage or
    holds it behind corrective orders. */
export type InspectionStatus = "requested" | "scheduled" | "ready" | "in-progress" | "passed" | "failed" | "closed";
export const INSPECTION_STATUS_LABEL: Record<InspectionStatus, string> = {
  requested: "Requested", scheduled: "Scheduled", ready: "Ready for inspection",
  "in-progress": "In progress", passed: "Passed", failed: "Corrections required", closed: "Closed",
};

/** The inspector's release decision at sign-off. */
export type ProceedDecision = "proceed" | "proceed-with-corrections" | "hold";
export const PROCEED_LABEL: Record<ProceedDecision, string> = {
  proceed: "Authorised to proceed",
  "proceed-with-corrections": "Proceed — corrections to follow",
  hold: "HOLD — do not proceed",
};

export type CorrectiveStatus = "open" | "done" | "verified" | "rejected";
/** A corrective order raised from an inspection: what to fix, by when, the
    contractor's proof it was done, and the inspector's verification. */
export interface CorrectiveOrder {
  id: string;
  ref: string;               // CO-012
  text: string;
  lineId?: string;
  sectionId?: string;
  raisedAt: string;
  raisedBy: string;
  due?: string;
  status: CorrectiveStatus;
  doneAt?: string;
  doneBy?: string;
  doneNote?: string;
  donePhotoIds?: string[];
  verifiedAt?: string;
  verifiedBy?: string;
  verifyNote?: string;
  /** The punch-list twin, so the punch register stays the single fix list. */
  punchRef?: string;
}

export interface InspectionPerson { name: string; role: string; company?: string }

export interface Inspection extends Trackable {
  id: string;
  ref: string;              // INS-012
  templateId?: string;
  templateName: string;
  category: ChecklistCategory;
  /** Snapshot of the template at execution time — later template edits must
      never rewrite a signed inspection. */
  sections: ChecklistSection[];
  location?: string;
  date: string;
  by: string;
  company?: string;
  witnessedBy?: string;
  lines: InspectionLine[];
  status: InspectionStatus;
  note?: string;
  signedBy?: string;
  signedAt?: string;
  translated?: boolean;
  originalText?: string;
  /** Library key of the construction aspect (rebar, blinding, columns…). */
  aspectKey?: string;
  /** Drawing sets the work was inspected against. */
  drawingIds?: string[];
  /* -- request → schedule → ready -------------------------------------- */
  requestedBy?: string;
  requestedAt?: string;
  requestedFor?: string;     // the contractor's preferred date
  requestNote?: string;
  scheduledFor?: string;     // ISO datetime the inspector booked
  scheduledBy?: string;
  scheduleNote?: string;
  readyAt?: string;
  readyBy?: string;
  readyNote?: string;
  readyPhotoIds?: string[];
  startedAt?: string;
  /* -- on site ---------------------------------------------------------- */
  /** Everyone present: inspector, contractor's rep, witness. */
  persons?: InspectionPerson[];
  /** Proof photos per phase (section id → photo ids). */
  sectionPhotos?: Record<string, string[]>;
  photoIds?: string[];
  /** The release decision. */
  authority?: { decision: ProceedDecision; by: string; at: string; note?: string };
  corrections?: CorrectiveOrder[];
  /** Set on a re-inspection raised after corrections. */
  reinspectionOf?: string;
  reinspectedBy?: string;
}


/* ===========================================================================
   SAVED VIEWS & SCHEDULED DELIVERY — a filter worth keeping, and a report
   that arrives on its own instead of waiting to be remembered.
   ========================================================================= */
export type RegisterKey =
  | "submittals" | "variations" | "correspondence" | "inspections"
  | "rfis" | "punch" | "observations" | "materials" | "tasks";

export const REGISTER_LABEL: Record<RegisterKey, string> = {
  submittals: "Submittals", variations: "Change & variations", correspondence: "Correspondence",
  inspections: "Inspections", rfis: "RFIs", punch: "Punch list",
  observations: "Observations", materials: "Materials", tasks: "Programme",
};

export interface SavedView {
  id: string;
  name: string;
  register: RegisterKey;
  /** Free-form filter bag the register page understands (status, ball, overdue…). */
  filters: Record<string, string>;
  createdBy: string;
  createdAt: string;
  shared?: boolean;         // visible to the whole project team
}

export type ScheduleCadence = "daily" | "weekly" | "monthly";

export interface ScheduledReport {
  id: string;
  name: string;
  kind: "open-items" | "client-digest" | "register" | "weekly-report" | "monthly-report";
  register?: RegisterKey;
  viewId?: string;
  cadence: ScheduleCadence;
  /** 1 = Monday … 7 = Sunday, for weekly. Day of month for monthly. */
  day?: number;
  hour: number;             // 0–23, project local
  recipients: string[];
  active: boolean;
  lastSentAt?: string;
  lastResult?: "delivered" | "failed" | "skipped";
  createdBy?: string;
}

export interface ProgressReport {
  id: string;
  title: string;
  period: string;          // "June 2026"
  summary: string;
  highlights: string[];
  progressPercent: number;
  issuedAt: string;
  /** Intelligent period reports (weekly/monthly compiler) carry the rest. A
      plain hand-drafted report leaves these unset and renders as before. */
  kind?: "weekly" | "monthly" | "manual";
  periodStart?: string;    // ISO date
  periodEnd?: string;      // ISO date
  preparedBy?: string;
  /** The compiled figures, so the report renders the same on any device and
      long after the underlying logs have scrolled away. */
  metrics?: PeriodMetrics;
  /** The narrative body — an executive summary plus analysed sections. */
  sections?: { heading: string; body: string; items?: string[] }[];
  /** The compiled record behind the figures — day-by-day attendance, the
      commercial position, programme analytics, safety, quality, procurement.
      Frozen at issue so the report never re-reads a project that has moved on. */
  detail?: PeriodFacts;
  /** Site photos across the period that demonstrate the progress described —
      resolved live from the synced photo store by id. */
  photoIds?: string[];
  /** AI vision descriptions per photo — an activity line for works photos, a
      headcount + disciplines note for team photos. Keyed by photo id. */
  photoNotes?: { id: string; text: string; team?: boolean }[];
  /** Whether AI wrote the narrative or the deterministic compiler did. */
  aiWritten?: boolean;
  /** Client sharing — issued reports can additionally be pushed to the client. */
  sharedWithClient?: boolean;
  sharedAt?: string;
}

/** A snapshot of how a week or month measured against the plan. */
/** Everything the period narrator and the report PDF need, in plain arrays.
    Persisted onto the issued ProgressReport so a report reads the same years
    later as it did on the day it was issued. */
export interface PeriodFacts {
    project: string; code: string; location: string; contractor: string;
    contractForm?: string;
    achievementsDone: string[];         // representative completed activities
    progressPhotos: string[];           // photo ids demonstrating that progress
    areas: { name: string; status?: string }[];
    weeklyCommitments: { text: string; met: boolean; detail?: string }[];
    /** The studio's goals for the period and how each one ended. Distinct from
        weeklyCommitments, which are the contractor's own — a report that shows
        only one of the two can always be read as flattering. */
    goals: {
      title: string; measure?: string; priority: "must" | "should";
      status: string; outcome?: string; sharedWithClient?: boolean;
      targetsMet?: number; targetsTotal?: number;
    }[];
    missedTargets: string[];
    lookAhead: string[];
    delays: { date: string; note: string }[];
    challenges: string[];
    openRfis: { ref: string; subject: string; overdue: boolean }[];
    materialsAwaited: string[];
    variations: { ref: string; title: string; status: string; cost?: number }[];
    instructions: { ref: string; subject: string }[];
    milestones: { title: string; date: string }[];
    behind: { name: string; progress: number }[];

    /* --- added: the analysed position, not just the diary ---------------- */
    /** Day-by-day attendance and weather, so the client can see the shape of
        the period rather than one averaged number. */
    days: { date: string; weather?: string; tempC?: number; head: number; activities: number; delayed: boolean }[];
    /** Crew split by trade across the period. */
    trades: { trade: string; peak: number; manDays: number }[];
    /** Where the money stands. Absent when nothing commercial is recorded. */
    commercial?: {
      currency: string; contractSum: number; revised: number;
      certifiedToDate: number; certifiedUnpaid: number; retentionHeld: number;
      pctCertified: number; remaining: number; valuationCount: number;
      approvedVariations: number; pendingVariations: number;
    };
    /** Earned value against the programme. */
    earnedValue?: { ev: number; pv: number; ac: number; cpi: number; spi: number; eac: number; vac: number; budget: number };
    /** Bill of quantities: billed vs placed, and any over-measure. */
    boq?: { billed: number; placed: number; pct: number; overRuns: { ref: string; description: string; excess: number }[] };
    /** Programme performance, from the analytics the dashboard already uses. */
    programme?: { actual: number; planned: number; variance: number; spi: number; forecastEnd: string; targetEnd: string; deltaDays: number; blocked: number; atRisk: number; phases: { phase: string; progress: number }[] };
    /** Monte-Carlo completion forecast and the risks driving it. */
    outlook?: { p50: string; p80: string; probOnTime: number; risks: string[] };
    /** Health & safety over the period and to date. */
    safety?: { inPeriod: number; incidents: number; nearMisses: number; reportable: number; daysSinceIncident: number | null; expiredPermits: number; records: { at: string; kind: string; title: string }[] };
    /** Quality: inspections closed in the period and the punch position. */
    quality?: { inspections: { ref: string; title: string; result: string; at: string }[]; passed: number; failed: number; punchRaised: number; punchClosed: number; punchOpen: number };
    /** Orders whose window has passed or is about to. */
    procurementRisk: { ref: string; item: string; supplier?: string; orderBy: string; slackDays: number; state: string }[];
    /** Extension-of-time position from the delay register. */
    eot?: { totalDays: number; criticalDays: number; claimedDays: number; grantedDays: number };
    /** Meetings held in the period, with their open actions. */
    meetings: { ref: string; title: string; date: string; openActions: number }[];
    /** Movement against the previous period, when a snapshot exists. */
    movement?: { label: string; from: number; to: number; better: boolean }[];
}

export interface PeriodMetrics {
  /** Goal accomplishment for the period. Null when no goals were set — which is
      itself worth printing, rather than showing a hollow 100%. */
  goalsTotal?: number;
  goalsMet?: number;
  goalsPartly?: number;
  goalsMissed?: number;
  goalsRate?: number | null;
  goalsMustTotal?: number;
  goalsMustMet?: number;
  progressNow: number;         // overall % complete at issue
  progressDelta?: number;      // change over the period, when known
  targetsTotal: number;
  targetsMet: number;
  targetsRate: number;         // %
  carried: number;             // targets carried/missed
  daysReported: number;
  daysInPeriod: number;
  avgManpower: number;
  peakManpower: number;
  manDays: number;
  activities: number;
  photos: number;
  delaysDays: number;
  rfisOpened: number;
  rfisAnswered: number;
  rfisOpen: number;
  submittalsMoved: number;
  variationsRaised: number;
  variationsValue?: number;
  materialsDelivered: number;
  observationsRaised: number;
  observationsClosed: number;
  instructionsReceived: number;
  behindTasks: number;
  milestonesDue: number;

  /* --- programme performance ------------------------------------------- */
  /** Time-weighted % the programme says should be complete by now. */
  plannedPercent?: number;
  /** Schedule performance index — actual ÷ planned. 1.0 is on plan. */
  spi?: number;
  /** Projected completion at the current pace, and how late that runs. */
  forecastEnd?: string;
  forecastDeltaDays?: number;
  blockedTasks?: number;
  atRiskTasks?: number;

  /* --- earned value ------------------------------------------------------ */
  earnedValue?: number;
  plannedValue?: number;
  actualCost?: number;
  cpi?: number;
  estimateAtCompletion?: number;
  varianceAtCompletion?: number;

  /* --- commercial -------------------------------------------------------- */
  contractSum?: number;
  revisedSum?: number;
  certifiedToDate?: number;
  certifiedUnpaid?: number;
  retentionHeld?: number;
  pctCertified?: number;
  variationsApprovedValue?: number;
  variationsPendingValue?: number;
  boqBilled?: number;
  boqPlaced?: number;
  boqOverRuns?: number;
  packagesUncommitted?: number;

  /* --- safety ------------------------------------------------------------ */
  safetyIncidents?: number;
  safetyNearMisses?: number;
  safetyReportable?: number;
  daysSinceIncident?: number | null;
  permitsExpired?: number;

  /* --- quality & close-out ---------------------------------------------- */
  inspectionsPassed?: number;
  inspectionsFailed?: number;
  punchRaised?: number;
  punchClosed?: number;
  punchOpen?: number;

  /* --- procurement & time ------------------------------------------------ */
  ordersLate?: number;
  ordersCritical?: number;
  eotDaysClaimed?: number;
  eotDaysGranted?: number;
  meetingsHeld?: number;
}

/* ===========================================================================
   COLLABORATION — the two-way workspace for what the CONTRACTOR is assisting
   with: information they owe, materials they're supplying, specs to submit,
   approvals to obtain. The studio assigns and chases; the contractor updates,
   uploads specs & photos, attaches quotes and requests approval. Material
   items can be pushed to the overseas material-sourcing app.
   ========================================================================= */
export type CollabKind = "information" | "material" | "spec" | "approval" | "action";
export type CollabStatus = "open" | "in-progress" | "submitted" | "approved" | "declined" | "closed";
export type CollabPriority = "normal" | "high" | "urgent";

export interface CollabUpdate {
  id: string;
  at: string;
  by: string;
  text: string;
  photoIds?: string[];
  fileIds?: { id: string; name: string }[];
  /** What kind of entry this is — drives the icon and colour in the thread. */
  event?: "update" | "follow-up" | "spec" | "approval-request" | "approval-granted" | "approval-declined" | "quote" | "status" | "sourcing";
  translated?: boolean;
  originalText?: string;
}

/** A supplier quotation attached to a confirmed item. */
export interface CollabQuote {
  id: string;
  supplier?: string;
  amount?: number;
  currency?: string;
  leadTime?: string;
  fileId?: string;
  fileName?: string;
  note?: string;
  at: string;
  by: string;
  accepted?: boolean;
}

export interface CollabItem {
  id: string;
  ref: string;              // COL-001
  title: string;
  kind: CollabKind;
  description: string;
  assignedTo: string;       // the contractor company / person
  assignedBy: string;       // the studio
  priority: CollabPriority;
  status: CollabStatus;
  dueDate?: string;
  ballInCourt: BallInCourt;
  createdAt: string;
  updatedAt: string;
  /** Times the studio has chased it, and when last. */
  followUps?: number;
  lastFollowUpAt?: string;
  /** Specs / documents the contractor has uploaded. */
  fileIds?: { id: string; name: string }[];
  photoIds?: string[];
  updates: CollabUpdate[];
  /** Approval flow. */
  approvalRequested?: boolean;
  approval?: { granted: boolean; by: string; at: string; note?: string };
  /** Quotations for a confirmed material/supply item. */
  quotes?: CollabQuote[];
  confirmed?: boolean;
  confirmedAt?: string;
  /** Bridge to the overseas material-sourcing app. */
  sourcing?: { projectId?: string; materialId?: string; url?: string; pushedAt?: string; status?: string };
  translated?: boolean;
  originalText?: string;
}

/* --- meetings ------------------------------------------------------------ */
export type MeetingKind = "progress" | "site" | "coordination" | "commercial" | "other";

/** An action raised against an agenda point during the meeting. */
export interface AgendaAction {
  id: string;
  text: string;
  assignee: string;
  due?: string;
  done: boolean;
}

export interface MeetingFollowUp {
  id: string;
  by: string;
  side: "studio" | "contractor";
  text: string;
  at: string;
  translated?: boolean;
  originalText?: string;
}

export interface AgendaItem {
  id: string;
  topic: string;
  note?: string;
  owner?: string;             // who brings the point
  status: "pending" | "discussed";
  outcome?: string;           // recorded while meeting on the point
  actions: AgendaAction[];
  /** Two-sided follow-up thread on this point — studio and contractor. */
  followUps?: MeetingFollowUp[];
  /** Marked resolved so it stops rolling over into the next meeting. */
  resolved?: boolean;
  /** This outcome was issued as a formal instruction — links the Correspondence. */
  instruction?: boolean;
  instructionRef?: string;
  /** Carried over from an earlier meeting where it was left open. */
  carried?: boolean;
  carriedFromRef?: string;
  /** Set when the contractor wrote it in Chinese and AI translated it. */
  translated?: boolean;
  originalText?: string;
}

/** A project meeting: agenda is planned up front, then the meeting is run
    point by point — outcomes and actions land against each item and the held
    record doubles as the minutes. */
export interface Meeting {
  id: string;
  ref: string;                // MTG-003
  title: string;
  kind: MeetingKind;
  date: string;               // ISO date
  time?: string;              // "10:30"
  location?: string;
  attendees: string;
  agenda: AgendaItem[];
  status: "planned" | "held";
  heldAt?: string;
  minutesIssuedAt?: string;
  by: string;
  createdAt: string;
  /** Video / call link — click to join. */
  link?: string;
  /** Ref of the earlier meeting whose open points were carried into this one. */
  rolledFrom?: string;
  /** AI-drafted minutes narrative, prepared from the recorded outcomes. */
  minutesSummary?: string;
}

/** The built-in levels. A member may instead carry a custom role's id, which
    is why TeamMember.role is widened to string — `isBuiltIn` tells them apart. */
export type TeamRole = "admin" | "contractor" | "consultant" | "technician" | "client" | "viewer";

export const BUILT_IN_ROLES: TeamRole[] = ["admin", "contractor", "consultant", "technician", "client", "viewer"];

export function isBuiltInRole(r: string): r is TeamRole {
  return (BUILT_IN_ROLES as string[]).includes(r);
}

export interface TeamMember {
  id: string;
  name: string;
  company: string;
  email: string;
  /** WhatsApp number — invites open straight to this contact's chat when set. */
  phone?: string;
  /** A built-in level, or the id of one of the project's own roles. */
  role: TeamRole | (string & {});
  /** For role "client": how much of the client portal this seat sees. */
  clientRole?: ClientRole;
  /** Discipline-scoped access — a subcontractor (e.g. an electrician) sees only
      these drawing disciplines. Empty/undefined = all (the main contractor). */
  drawingDisciplines?: string[];
  accessCode: string;
  invitedAt: string;
  status: "invited" | "active";
}

/** A request for an access code, raised from the landing page and decided by
    the manager in the Access section. */
export interface AccessRequest {
  id: string;
  name: string;
  company: string;
  email: string;
  desired: "bidder" | "client" | "project";
  targetKind: "tender" | "project";
  targetId: string;
  targetLabel: string;
  /** Project-team requests: the role the requester asked for — the manager
      can still change it at approval. */
  role?: TeamRole;
  message?: string;
  status: "pending" | "approved" | "declined";
  code?: string;           // issued on approval
  createdAt: string;
  decidedAt?: string;
}

/** Structured project facts shown on the dashboard. */
export interface ProjectDetails {
  siteAddress?: string;
  plotArea?: string;       // "2.4 ha"
  builtArea?: string;      // "3,600 m² GFA"
  scope?: string;          // "18 duplex units + pavilion"
  procurement?: string;    // "Measured / re-measurable BOQ"
  contractForm?: string;   // "FIDIC Red Book 2017"
}

/** Which portal tabs the client can see — controlled by the manager. */
export interface ClientViewConfig {
  programme: boolean;
  reports: boolean;
  gallery: boolean;
  diary: boolean;
  /** View-only PM sections. Legacy configs without the key default to shown. */
  targets?: boolean;
  instructions?: boolean;
  materials?: boolean;
  /** Current issued drawing set — full-size viewer + downloads. Legacy configs
      without the key default to shown (matches the tender portal). */
  drawings?: boolean;
}

export interface ClientVisit { at: string; name: string; }

/** The marks that go on an issued document. Held as compressed data URIs so a
    report embeds them without a network fetch — a PDF opened in five years
    still carries its letterhead. Kept small deliberately; these are letterhead
    marks, not artwork. */
export interface ProjectBranding {
  studioLogo?: string;
  contractorLogo?: string;
  studioLogoAt?: string;
  contractorLogoAt?: string;
  /** Who the contractor mark belongs to, printed beneath it. */
  contractorName?: string;
}

/** Framing for a cover image: the focal point as a percentage of the image,
    and how far it is zoomed past "cover". Defaults read as centred at 100%. */
export interface RenderCrop {
  x: number;        // 0–100, horizontal focal point
  y: number;        // 0–100, vertical focal point
  scale: number;    // 1 = fill, >1 zooms in
}

/* ===========================================================================
   VALUATIONS — what is actually owed this month. A schedule of values, the
   work done against it, materials on site, retention and what was already
   certified; the net is the figure the payment certificate is written for.
   ========================================================================= */
export interface SovLine {
  id: string;
  ref?: string;              // BOQ / trade reference
  description: string;
  /** Contract value of this line — the sum of these should equal the sum. */
  value: number;
  /** Percentage of this line complete at this valuation. */
  percent?: number;
}

export type ValuationStatus = "draft" | "submitted" | "certified" | "paid";

export const VALUATION_STATUS_LABEL: Record<ValuationStatus, string> = {
  draft: "Being prepared", submitted: "Submitted for certification",
  certified: "Certified", paid: "Paid",
};

export interface Valuation {
  id: string;
  ref: string;               // IPC-004
  no: number;                // 4
  periodTo: string;          // valuation date (ISO)
  status: ValuationStatus;
  /** Snapshot of the schedule at this valuation, so history never rewrites. */
  lines: SovLine[];
  /** Gross value of measured work at this date. */
  workDone: number;
  /** Approved variations included, taken from the variation register. */
  variationsValue: number;
  materialsOnSite?: number;
  /** Reimbursable expenses carried on this valuation — a snapshot, with the
      mark-up applied, so the certificate never changes after the fact. */
  expenses?: { id: string; ref: string; date: string; description: string; amount: number; markupPercent?: number; billed: number }[];
  expensesValue?: number;
  retentionPercent?: number;
  /** Everything certified in previous valuations — this one pays the balance. */
  previouslyCertified?: number;
  note?: string;
  preparedBy: string;
  preparedAt: string;
  certifiedBy?: string;
  certifiedAt?: string;
  paidAt?: string;
  /** Set once handed to the Contract Generator to raise the certificate. */
  certificateRef?: string;
  /* -- the bill as the client sees it ------------------------------------- */
  /** Invoice number on the document; defaults to <project code>-<ref>. */
  invoiceNo?: string;
  /** When payment is due — set on certification from the project's terms. */
  dueDate?: string;
  /** Every time the invoice was emailed to the client, with the PDF kept. */
  sentToClient?: { id: string; at: string; to: string[]; by: string; fileId?: string }[];
  /** Automatic chases — once before it is due, then weekly while overdue. */
  dueRemindedOn?: string;
  overdueRemindedOn?: string;
}

/* ===========================================================================
   EXPENSES — money laid out on the client's behalf: a permit fee, a courier,
   a hire, a flight to site. Logged in seconds with the receipt photographed,
   approved by the studio, and carried onto the next valuation as a
   reimbursable line so it is billed, not forgotten.
   ========================================================================= */
export type ExpenseCategory = "permits" | "travel" | "subsistence" | "printing" | "materials" | "hire" | "fees" | "courier" | "testing" | "other";
export const EXPENSE_CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  permits: "Permits & statutory", travel: "Travel & fuel", subsistence: "Meals & lodging", printing: "Printing & plans",
  materials: "Materials bought", hire: "Plant & tool hire", fees: "Consultant & test fees", courier: "Courier & freight", testing: "Testing & samples", other: "Other",
};
export type ExpenseStatus = "logged" | "approved" | "billed" | "reimbursed" | "rejected";
export const EXPENSE_STATUS_LABEL: Record<ExpenseStatus, string> = {
  logged: "Logged", approved: "Approved to bill", billed: "On a valuation", reimbursed: "Reimbursed", rejected: "Not reimbursable",
};
export interface Expense {
  id: string;
  ref: string;                 // EXP-012
  date: string;                // when the money was spent
  category: ExpenseCategory;
  description: string;
  amount: number;              // in the project currency
  vendor?: string;
  paidBy: string;              // who is out of pocket
  paidByCompany?: string;
  /** Carried to the client with this mark-up (handling), when agreed. */
  markupPercent?: number;
  reimbursable: boolean;       // false = a cost the studio absorbs, kept for the record
  receiptPhotoIds?: string[];
  note?: string;
  status: ExpenseStatus;
  loggedBy: string;
  loggedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedNote?: string;
  /** The valuation it was billed on, once carried across. */
  valuationId?: string;
  reimbursedAt?: string;
}

/** How the studio gets paid — printed on every invoice. */
export interface BillingDetails {
  terms?: number;              // days from certification to due, default 30
  accountName?: string;
  bankName?: string;
  branch?: string;
  accountNo?: string;
  swift?: string;
  address?: string;            // the studio's billing address
  taxId?: string;
  notes?: string;              // "Please quote the invoice number", late-payment terms…
}

/* ===========================================================================
   SITE TELEMETRY — deliveries, plant hours and gate movements arriving from
   the site itself rather than being typed in the evening. Every row carries
   how it got here, because an automatic reading and a human one are not the
   same evidence.
   ========================================================================= */
export type TelemetryKind = "delivery" | "plant" | "gate";
export type TelemetrySource = "manual" | "gate-scan" | "device" | "api";

export const TELEMETRY_KIND_LABEL: Record<TelemetryKind, string> = {
  delivery: "Delivery", plant: "Plant hours", gate: "Gate movement",
};

export interface TelemetryEvent {
  id: string;
  kind: TelemetryKind;
  at: string;                 // ISO datetime
  source: TelemetrySource;
  /** Deliveries: what arrived, from whom, on what ticket. */
  item?: string;
  supplier?: string;
  ticketRef?: string;
  qty?: number;
  unit?: string;
  /** Plant: the machine and the hours run. */
  plant?: string;
  hours?: number;
  /** Gate: who moved, and which way. */
  who?: string;
  company?: string;
  direction?: "in" | "out";
  note?: string;
  /** The material request this delivery satisfies, where it could be matched. */
  materialId?: string;
}

/* ===========================================================================
   FOREIGN CURRENCY — overseas supply is priced abroad and valued at home. The
   rate used is kept with the record, so a later swing never rewrites history.
   ========================================================================= */
export interface FxRate {
  /** ISO code as quoted — "CNY", "USD". */
  code: string;
  /** How many units of the CONTRACT currency one unit of `code` buys. */
  rate: number;
  setAt: string;
  setBy: string;
  note?: string;
}

/** A price in a currency that isn't the contract's, with the conversion used. */
export interface ForeignPrice {
  amount: number;
  code: string;
  /** The rate applied at the moment it was recorded — frozen deliberately. */
  rate: number;
  /** amount × rate, in the contract currency. */
  converted: number;
  at: string;
}

/* ===========================================================================
   PROJECT TEMPLATES — the structure of a finished job, saved so the next one
   starts from experience rather than a blank page. Structure only: never the
   people, the money agreed, or anything that happened.
   ========================================================================= */
export interface ProjectTemplate {
  id: string;
  name: string;
  description?: string;
  /** The project it was captured from, for provenance. */
  fromCode?: string;
  createdAt: string;
  createdBy: string;
  /** Programme skeleton — durations and dependencies, dates rebased on use. */
  tasks?: { name: string; phase: string; offsetDays: number; durationDays: number; owner?: string; dependsOnNames?: string[]; milestone?: boolean }[];
  /** Bill items with rates but no measure. */
  boq?: { ref: string; section: string; description: string; unit: string; qty: number; rate: number }[];
  /** Trade packages as scopes, without a subcontractor or a value. */
  packages?: { name: string; trade: string; scope?: string }[];
  checklists?: ChecklistTemplate[];
  /** Drawing numbers and titles — the register the studio expects to produce. */
  drawings?: { number: string; title: string; discipline: string }[];
  /** Roles to invite, by role key — never actual people. */
  roles?: string[];
}

/* ===========================================================================
   CLIENT APPROVALS — a decision the employer signs for, with the signature,
   the name typed, and the moment it happened. What makes a variation agreed
   rather than merely sent.
   ========================================================================= */
export type ApprovalTarget = "variation" | "valuation" | "instruction" | "document";

export const APPROVAL_TARGET_LABEL: Record<ApprovalTarget, string> = {
  variation: "Variation", valuation: "Payment certificate",
  instruction: "Instruction", document: "Document",
};

export interface ClientApproval {
  id: string;
  target: ApprovalTarget;
  /** The record's own id, and its ref for the human record. */
  recordId: string;
  ref: string;
  title: string;
  /** What was actually agreed — the amount or days, frozen at signing. */
  amount?: number;
  days?: number;
  currency?: string;
  requestedAt: string;
  requestedBy: string;
  /** Set on signature. */
  decision?: "approved" | "rejected";
  decidedAt?: string;
  /** The name typed by the signatory — their assertion of who they are. */
  signedName?: string;
  /** PNG data URI of the drawn signature. */
  signature?: string;
  note?: string;
  /** Where it was signed from, for the audit trail. */
  agent?: string;
}

/* ===========================================================================
   BILL OF QUANTITIES — measured work against rates. Valuations built from
   quantities actually placed rather than a percentage anyone guessed at.
   ========================================================================= */
export interface BoqItem {
  id: string;
  ref: string;                 // "B.2.03" — the bill reference
  section: string;             // "Substructure"
  description: string;
  unit: string;                // m³, m², nr, item, kg
  qty: number;                 // the billed quantity
  rate: number;
  /** Quantity measured as placed to date. Drives the valuation. */
  placed?: number;
  /** The programme activity this item is measured against, where linked. */
  taskId?: string;
  /** Trade package this item belongs to, when the work is sublet. */
  packageId?: string;
  note?: string;
}

/* ===========================================================================
   TRADE PACKAGES — a sublet scope with its own account: what it covers, what
   it's worth, what has been valued and what is retained. Kept back-to-back
   with the main contract so the main account and the sublet accounts agree.
   ========================================================================= */
export type PackageStatus = "draft" | "let" | "on-site" | "complete" | "closed";

export const PACKAGE_STATUS_LABEL: Record<PackageStatus, string> = {
  draft: "Being scoped", let: "Let", "on-site": "On site", complete: "Complete", closed: "Account closed",
};

export interface SubAttachment { fileId: string; name: string }

export interface PackagePayment {
  id: string;
  ref: string;                 // "PAY-002"
  periodTo: string;
  /** Gross value of the sub's work at this date — the CLAIMED figure when the
      row arrives as a subcontractor's payment request. */
  gross: number;
  retentionPercent?: number;
  previouslyPaid?: number;
  /** "requested" = submitted by the subcontractor from their room, awaiting
      the studio's certificate. The other three are the studio-side lifecycle
      that always existed. */
  status: "requested" | "draft" | "certified" | "paid";
  note?: string;
  at: string;
  by: string;
  /** Request-side detail — who asked, their backup, and whether they want the
      work valued on site rather than certified off the claim. */
  requestedBy?: string;
  attachments?: SubAttachment[];
  valuationRequested?: boolean;
  /** The certified figure when the studio certifies less (or more) than was
      claimed; absent means certified as claimed. */
  certifiedGross?: number;
  certifiedBy?: string;
  certifiedAt?: string;
}

/* --- the subcontract room -------------------------------------------------
   A trade package can be run as a full subcontract: an itemised scope built
   (with AI assistance) from the specification, priced by invited subs from a
   limited room, awarded, then progressed, valued and settled — all against
   the same scope lines. */

export interface ScopeItem {
  id: string;
  ref: string;               // "1.03"
  description: string;
  qty?: number;
  unit?: string;
  /** Governing spec section / drawing note this item is built to. */
  specRef?: string;
  /** Sub-reported progress 0–100 — a claim until the studio checks it off. */
  progress?: number;
  /** Studio check-off: built, seen and accepted. */
  done?: boolean;
  doneBy?: string;
  doneAt?: string;
}

export interface SubInvite {
  id: string;
  company: string;
  contact: string;
  email: string;
  phone?: string;
  accessCode: string;
  invitedAt: string;
  status: "invited" | "active" | "declined";
  lastActive?: string;
  /** Why they were invited: to price the package (default) or to set out
      their own work programme in the room. */
  purpose?: "price" | "programme";
}

export interface SubQuoteLine { scopeItemId: string; amount: number; note?: string }

export interface SubQuote {
  id: string;
  inviteId: string;
  by: string;
  company: string;
  at: string;
  lines: SubQuoteLine[];
  /** Priced items that aren't scope lines — prelims, attendances, cranage. */
  extras?: { description: string; amount: number }[];
  exclusions?: string;
  programmeDays?: number;
  note?: string;
  status: "submitted" | "revised" | "accepted" | "declined";
}

export interface SubClaim {
  id: string;
  ref: string;               // "CLM-02"
  title: string;
  detail: string;
  amount?: number;
  at: string;
  by: string;
  attachments?: SubAttachment[];
  status: "open" | "assessed" | "settled" | "rejected";
  response?: string;
  respondedBy?: string;
  respondedAt?: string;
  settledAmount?: number;
}

export const SUB_CLAIM_STATUS_LABEL: Record<SubClaim["status"], string> = {
  open: "Open", assessed: "Assessed", settled: "Settled", rejected: "Rejected",
};

/** What of the project an invited sub sees beyond their own scope — chosen
    per package, so a sub is exposed to exactly what their price needs. */
export interface SubExposure {
  /** The selected drawings (docIds on the package) — never the whole register. */
  drawings?: boolean;
  /** The programme's headline dates and their own activities. */
  program?: boolean;
  /** Site photographs. */
  gallery?: boolean;
}

export interface TradePackage {
  id: string;
  ref: string;                 // "PKG-STR"
  name: string;                // "Structural steel"
  trade: string;
  subcontractor?: string;      // company
  contact?: string;
  email?: string;
  phone?: string;
  status: PackageStatus;
  /** The sublet order value. */
  value: number;
  retentionPercent?: number;
  scope?: string;
  /** Programme activities this package is responsible for — its scope. */
  taskIds?: string[];
  /** Main-programme activities (outside the scope) whose state holds this
      package up — the predecessor trades, access, a slab that must be cast. */
  blockedByTaskIds?: string[];
  /** The subcontractor's own parallel schedule, when one is kept. */
  schedule?: SubTask[];
  startOn?: string;
  finishOn?: string;
  payments?: PackagePayment[];
  createdAt: string;
  createdBy: string;
  /* --- subcontract room (all optional — a plain package still works) ----- */
  /** Division of works this package sits in — "26 · Electrical". */
  division?: string;
  /** The governing specification extract shown to every invited sub. */
  specNotes?: string;
  /** The itemised scope — priced line by line, progressed, checked off. */
  scopeItems?: ScopeItem[];
  /** Drawings this package is priced and built against — the ONLY sheets an
      invited sub is exposed to. */
  docIds?: string[];
  exposure?: SubExposure;
  invites?: SubInvite[];
  quotes?: SubQuote[];
  awardedInviteId?: string;
  claims?: SubClaim[];
}

/* ===========================================================================
   HEALTH & SAFETY — toolbox talks, permits to work, inductions and incidents.
   The statutory spine of a site, kept where the rest of the record is.
   ========================================================================= */
export type SafetyKind = "toolbox-talk" | "induction" | "permit" | "incident" | "near-miss" | "inspection" | "drill";

export const SAFETY_KIND_LABEL: Record<SafetyKind, string> = {
  "toolbox-talk": "Toolbox talk", induction: "Site induction", permit: "Permit to work",
  incident: "Incident", "near-miss": "Near miss", inspection: "Safety inspection", drill: "Emergency drill",
};

export type SafetySeverity = "none" | "first-aid" | "medical" | "lost-time" | "major";

export const SEVERITY_LABEL: Record<SafetySeverity, string> = {
  none: "No injury", "first-aid": "First aid", medical: "Medical treatment",
  "lost-time": "Lost time", major: "Major / reportable",
};

export interface SafetyRecord {
  id: string;
  ref: string;                 // "HS-014"
  kind: SafetyKind;
  title: string;
  detail?: string;
  at: string;                  // when it happened / was held
  by: string;
  company?: string;
  location?: string;
  /** Who attended a talk or induction — names as given. */
  attendees?: string[];
  /** Permits only: the window it is valid for, and whether it's been closed. */
  validFrom?: string;
  validTo?: string;
  closedAt?: string;
  /** Incidents / near misses only. */
  severity?: SafetySeverity;
  /** Reportable to the authority (RIDDOR-equivalent). */
  reportable?: boolean;
  reportedAt?: string;
  actionsTaken?: string;
  photoIds?: string[];
  /** Corrective actions raised — mirrored onto the punch list where relevant. */
  followUp?: string;
  followUpDone?: boolean;
}

/* ===========================================================================
   CONTRACT CLAUSES — the governing conditions, indexed so any claim, EOT or
   variation can cite the clause it stands on rather than an assertion.
   ========================================================================= */
export interface ContractClause {
  id: string;
  number: string;            // "20.1"
  title: string;
  /** The operative text, or a faithful précis of it. */
  text: string;
  /** What this clause governs — used to match it to a record. */
  topics?: string[];         // ["eot", "weather", "notice"]
  /** Days within which notice must be given, where the clause sets one. */
  noticeDays?: number;
}

export interface ContractIndex {
  form: string;              // "FIDIC Red Book 2017" / "Bespoke"
  sourceDocId?: string;      // the ProjectDocument it was read from
  indexedAt: string;
  indexedBy: string;
  clauses: ContractClause[];
}

/** A live executive view published to the employer on an unguessable link.
    Read-only and deliberately thin — headline progress, milestones, photos,
    and only the commercial lines the studio chooses to show. */
export interface ExecShare {
  token: string;
  createdAt: string;
  createdBy: string;
  /** Whether the money is on the page at all. Off by default. */
  showCost?: boolean;
  showPhotos?: boolean;
  showForecast?: boolean;
  note?: string;
  viewedAt?: string;
  views?: number;
  revokedAt?: string;
}

/* ===========================================================================
   SNAPSHOTS — a frozen reading of where the project stood on a date, so any
   later position can be read as movement rather than an absolute. Small by
   design: headline figures only, never a copy of the whole project.
   ========================================================================= */
export interface ProjectSnapshot {
  id: string;
  at: string;                  // ISO datetime the reading was taken
  label: string;               // "Week 30" / "End of July"
  by: string;
  auto?: boolean;              // taken by the scheduled sweep, not by hand
  /* programme */
  progress: number;            // % complete, time-weighted
  planned: number;             // % that should have been complete
  spi: number;
  forecastEnd: string;
  blocked: number;
  /* commercial */
  revisedSum: number;
  approvedVariations: number;
  pendingVariations: number;
  certified: number;
  /* registers — open counts */
  openRfis: number;
  openSubmittals: number;
  openPunch: number;
  openObservations: number;
  openMaterials: number;
}

/* ===========================================================================
   WEATHER DELAYS — inclement-weather days that cost time on site, logged from
   the daily record and compiled into an extension-of-time claim.
   ========================================================================= */
export interface DelayDay {
  id: string;
  date: string;              // ISO date
  weather: Weather;
  /** EOT days claimed for this event — often 0.5 or 1 per bad day. */
  claimedDays: number;
  /** Whether it hit the critical path (only then does it move completion). */
  criticalImpact: boolean;
  tasksAffected?: string[];  // activity names impacted
  note?: string;
  loggedBy: string;
  loggedAt: string;
  includeInEot?: boolean;    // selected into the next EOT claim
  /** The correspondence ref once compiled into an issued EOT claim. */
  eotRef?: string;
}

/* ===========================================================================
   CUSTOM ROLES — the five built-in levels cover most projects, but a QS, a
   clerk of works or a safety officer sit awkwardly in any of them. A project
   can define its own roles and tick exactly what each may do; the built-in
   five can be overridden the same way without being deleted.
   ========================================================================= */
export interface CustomRole {
  /** Stable key used on TeamMember.role — "role-qs". Never reused. */
  id: string;
  name: string;               // "Quantity Surveyor"
  description?: string;
  /** Capability key → granted. Absent keys mean not granted. */
  grants: Record<string, boolean>;
  /** Works across every project like a Site Admin. Off for most roles. */
  fullAccess?: boolean;
  createdAt: string;
  createdBy?: string;
}

/** Per-project overrides of the built-in roles' default matrix. Keyed by the
    built-in role, then by capability; anything absent keeps the default. */
export type RoleOverrides = Record<string, Record<string, boolean>>;

/* --- project document library --------------------------------------------- */
export type DocFolder =
  | "permits" | "insurance" | "bonds" | "contract" | "warranties"
  | "method-statements" | "test-certificates" | "handover" | "other";

export const DOC_FOLDER_LABEL: Record<DocFolder, string> = {
  permits: "Permits & approvals", insurance: "Insurance", bonds: "Bonds & guarantees",
  contract: "Contract", warranties: "Warranties", "method-statements": "Method statements",
  "test-certificates": "Test certificates", handover: "Handover & O&M", other: "Other",
};

/** Folders whose contents lapse — these are the ones worth chasing. */
export const EXPIRING_FOLDERS: DocFolder[] = ["permits", "insurance", "bonds", "warranties"];

/* ===========================================================================
   SITE INVENTORY — the things on site that someone is responsible for: plant,
   tools, instruments, keys, devices, stock. Each item knows who holds it now,
   every hand it has passed through, and the signed transfer that moved it.
   ========================================================================= */
export type InventoryCategory = "plant" | "tool" | "instrument" | "device" | "key" | "material" | "ppe" | "other";
export const INVENTORY_CATEGORY_LABEL: Record<InventoryCategory, string> = {
  plant: "Plant & equipment", tool: "Tools", instrument: "Survey & test instruments", device: "Phones, tablets, cameras",
  key: "Keys & access", material: "Material stock", ppe: "PPE", other: "Other",
};
export type InventoryCondition = "good" | "fair" | "damaged" | "unserviceable";
export const INVENTORY_CONDITION_LABEL: Record<InventoryCondition, string> = { good: "Good", fair: "Fair", damaged: "Damaged", unserviceable: "Unserviceable" };
export type InventoryStatus = "on-site" | "off-site" | "in-store" | "lost" | "disposed";
export const INVENTORY_STATUS_LABEL: Record<InventoryStatus, string> = { "on-site": "On site", "off-site": "Off site", "in-store": "In store", lost: "Lost / stolen", disposed: "Disposed" };

/** Who has the item — a person, with the firm they act for. */
export interface Holder { name: string; company?: string; role?: string }

/** A drawn signature with the name typed beside it — the assertion of who signed. */
export interface Signature { name: string; dataUri: string; at: string; company?: string }

/** One line of an item's possession history. */
export interface PossessionEntry {
  id: string;
  at: string;
  kind: "added" | "transferred" | "checked" | "returned" | "status";
  holder: Holder;
  location?: string;
  condition?: InventoryCondition;
  note?: string;
  by: string;
  transferId?: string;
  photoIds?: string[];
}

export type TransferStatus = "pending" | "completed" | "declined" | "cancelled";
/** A hand-over: the giver and the receiver both sign, and only then does
    possession move. Until then the item stays with the giver. */
export interface InventoryTransfer {
  id: string;
  ref: string;                 // TR-012
  from: Holder;
  to: Holder;
  requestedAt: string;
  requestedBy: string;
  location?: string;
  condition?: InventoryCondition;
  note?: string;
  photoIds?: string[];
  status: TransferStatus;
  fromSignature?: Signature;
  toSignature?: Signature;
  completedAt?: string;
  declinedAt?: string;
  declineNote?: string;
  /** Email the receiver was asked to sign at, when one was sent. */
  notifiedAt?: string;
}

export interface InventoryItem extends Trackable {
  id: string;
  ref: string;                 // INV-012
  name: string;
  category: InventoryCategory;
  description?: string;
  serial?: string;             // serial / asset tag / plate
  qty: number;
  unit?: string;
  owner?: string;              // whose asset it is — the contractor, the studio, hired
  status: InventoryStatus;
  condition?: InventoryCondition;
  location?: string;
  holder: Holder;
  heldSince: string;
  possession: PossessionEntry[];   // oldest → newest
  transfers: InventoryTransfer[];
  photoIds?: string[];
  addedAt: string;
  addedBy: string;
  archived?: { at: string; by: string };
}

/** One version of a CAD file — the file as it was sent, with who, when and why. */
export interface CadVersion {
  id: string;
  fileId: string;
  fileName: string;
  size?: string;
  sha?: string;
  note?: string;
  uploadedAt: string;
  by: string;
  /** Set when an older version was made current again. */
  restoredFrom?: string;
}
export interface CadFile {
  id: string;
  name: string;
  discipline?: string;
  description?: string;
  versions: CadVersion[];          // oldest → newest; the last is current
  /** Sheets on the drawing register this file produces. */
  sheetIds?: string[];
  archived?: { at: string; by: string };
}

export interface ProjectDocument {
  id: string;
  name: string;
  folder: DocFolder;
  fileId: string;
  fileName: string;
  size?: string;
  note?: string;
  /** The date it stops being valid — insurance, bonds, permits. */
  expiresOn?: string;
  issuedBy?: string;
  uploadedAt: string;
  uploadedBy: string;
  /** Visible in the client portal. Off by default — most of this is internal. */
  sharedWithClient?: boolean;
}

/* --- coordination models ------------------------------------------------- */
export type ModelFormat = "glb" | "gltf" | "ifc" | "other";

export const MODEL_FORMAT_LABEL: Record<ModelFormat, string> = {
  glb: "glTF binary (.glb)", gltf: "glTF (.gltf)", ifc: "IFC", other: "Model file",
};

/** A coordination model issued to site. glTF/GLB opens in the browser viewer;
    IFC is carried for download and hand-off to the contractor's own tools. */
export interface BimModel {
  id: string;
  name: string;
  discipline?: string;
  format: ModelFormat;
  revision: string;
  fileId: string;
  fileName: string;
  size?: string;
  note?: string;
  uploadedAt: string;
  uploadedBy: string;
  /** Hidden from the contractor while the studio is still coordinating it. */
  issued?: boolean;
}

/* ---- client comms: things we need FROM the client, and the thread ---- */
export type ClientRequestStatus = "open" | "provided" | "accepted";
/** Something the studio needs the client to supply — a decision, an approval,
    a document. The client marks it provided (optionally attaching a file);
    the studio accepts it to close the loop. */
export interface ClientRequest {
  id: string;
  ref: string;                 // REQ-01, per project
  title: string;
  detail?: string;
  dueOn?: string;
  status: ClientRequestStatus;
  requestedOn: string;
  requestedBy: string;
  providedOn?: string;
  providedNote?: string;
  fileId?: string;
  fileName?: string;
  acceptedOn?: string;
  /** Follow-ups already sent, so the chase history is on the record. */
  chasedOn?: string[];
  /** Last automatic overdue reminder — throttles the scheduled chase. */
  remindedOn?: string;
}
/** One message in the studio ↔ client thread. */
export interface ClientMessage {
  id: string;
  from: "studio" | "client";
  authorName: string;
  body: string;
  createdOn: string;
  requestId?: string;          // when the message is about a specific request
}

export interface Project {
  id: string;
  code: string;            // PRJ-2601
  name: string;
  location: string;
  /** Site coordinates — drives the daily weather feed. */
  geo?: { lat: number; lon: number };
  /** IANA zone for the SITE (e.g. "America/Guyana", "Asia/Shanghai").
      Every "today" on this project — the daily report's date, the day's
      targets, which days count as missed — is resolved in this zone, not on
      whatever clock the filer's phone happens to be set to. Overseas crews
      routinely leave their phones on their home time, which silently dated
      their reports a day ahead. Unset falls back to the site's longitude,
      then to the device. */
  timezone?: string;
  details?: ProjectDetails;
  description: string;
  hue: number;
  currency: string;
  contractSum?: number;
  client: Party;
  contractor: Party & { accessCode: string };
  team: TeamMember[];
  startDate: string;
  targetCompletion: string;
  milestones?: ProjectMilestone[];   // critical dates — dashboard counts down to the next
  /** Set once the start-up task bundle has been sent to the Mind Map. */
  onboarding?: { sentAt: string; sheetTitle?: string; hubId?: string; tasks: number; assignee?: string };
  /** The Mind Map board that contractor-raised RFIs are pushed to. */
  rfiBoard?: { sheetId: string; hubId: string };
  /** Ad-hoc to-dos anyone on the project can add — the contractor's own task list. */
  adhocTasks?: AdhocTask[];
  programmeUpdatedAt?: string;
  /** Studio's open ask for the current work programme — cleared visually the
      moment an import lands after it. */
  programmeRequest?: { at: string; by: string };
  programmeUpdatedBy?: string;
  drawings: DrawingSet[];
  /** Sheets removed from the register, kept 30 days so a removal can be undone. */
  drawingTrash?: { set: DrawingSet; deletedAt: string; by: string }[];
  /** Ids of records this project has deliberately removed (merged duplicates,
      binned sheets). The server merges project writes record by record and
      would otherwise bring a removed record straight back from the copy it
      already holds; an id listed here stays gone on every device. */
  recordTombstones?: string[];
  rfis: PmRfi[];
  /** What is holding the job up right now — see Bottleneck. */
  bottlenecks?: Bottleneck[];
  /** Long-lead materials and equipment, tied to the activities they gate. */
  longLead?: LongLeadItem[];
  /** Technical approvals — shop drawings, samples, method statements. */
  submittals?: Submittal[];
  /** Change events and the variations they become. */
  variations?: Variation[];
  /** Instructions, notices and claims — the formal paper trail. */
  correspondence?: Correspondence[];
  /** The studio's weekly goals, handed to the contractor and assessed at the
      end of the week. Separate from `targets`, which the contractor owns. */
  goals?: WeeklyGoal[];
  /** Planning probe: while on, the server chases the contractor from Friday
      until the COMING week has committed targets — the same standing pressure
      the daily-report reminder applies, aimed a week ahead. */
  planProbe?: boolean;
  lastPlanNudge?: string;
  /** Dated probes the studio sets: a chase that fires on a chosen day, most
      often ahead of a milestone ("confirm slab-pour readiness"). The server
      emails it once when the date arrives; the studio can also fire it by
      hand — email or WhatsApp — at any time. */
  dateProbes?: DateProbe[];
  /** Reusable checklists and the inspections run from them. */
  checklists?: ChecklistTemplate[];
  inspections?: Inspection[];
  /** Kept filters and the reports that go out on their own. */
  savedViews?: SavedView[];
  schedules?: ScheduledReport[];
  /** Morning client email: every day the previous day's report goes to the
      client automatically. Default ON — explicit false pauses it. */
  clientDailyEmail?: boolean;
  logs: DailyLog[];
  /** Inclement-weather delay register — days lost, and the EOT claims built. */
  delays?: DelayDay[];
  /** Frozen period readings — the basis for every "vs last month" comparison. */
  snapshots?: ProjectSnapshot[];
  /** A live executive summary published to the employer on a tokened link. */
  execShare?: ExecShare;
  /** The governing contract, indexed clause by clause. */
  contractIndex?: ContractIndex;
  /** Measured work against rates — the basis for valuing what's been placed. */
  boq?: BoqItem[];
  /** Sublet trade packages and their back-to-back accounts. */
  packages?: TradePackage[];
  /** Optional programme tooling the studio switches on per project. */
  programmeFeatures?: { subSchedules?: boolean };
  /** Toolbox talks, permits, inductions and incidents. */
  safety?: SafetyRecord[];
  /** Decisions the employer has signed for. */
  approvals?: ClientApproval[];
  /** Exchange rates in force for this project, newest per code wins. */
  fx?: FxRate[];
  /** Deliveries, plant hours and gate movements arriving from site. */
  telemetry?: TelemetryEvent[];
  /** An ingest key for this project only, so a device can post without a session. */
  telemetryKey?: string;
  /** When the nightly sweep last emailed the contractor about un-filed daily
      reports. Throttles the chase so a standing gap doesn't mail every day. */
  lastReportNudge?: string;
  /** Fixed camera positions the daily report asks for by name. */
  viewpoints?: SiteViewpoint[];
  /** Which daily-report fields this project insists on. */
  reportRules?: DailyReportRules;
  observations: Observation[];
  visits: SiteVisit[];
  meetings: Meeting[];
  tasks: GanttTask[];
  programmeNotes?: TaskNote[];   // programme-wide comments / queries / snags / risks
  /** Lifecycle phase — drawings/content nest under it; advances forward only. */
  phase?: ProjectPhase;
  phaseAdvancedAt?: string;
  /** Drawing packages shared out on tokened links. */
  drawingShares?: DrawingShare[];
  targets: DailyTarget[];
  materials: MaterialRequest[];
  punch: PunchItem[];
  /** Building-services register — MEP systems from first fix to commissioning. */
  mep?: MepSystem[];
  reports: ProgressReport[];
  /** Collaboration register — obligations the contractor is assisting with:
      information, materials to supply, specs, approvals. */
  collab?: CollabItem[];
  /** The matching project id in the overseas material-sourcing app, once a
      collaboration material has been pushed there (reused for later ones). */
  sourcingProjectId?: string;
  activity: Activity[];
  clientView?: ClientViewConfig;
  clientSeen?: ClientVisit[];   // newest first, capped
  /** What the studio has asked the client to provide, and the shared thread. */
  clientRequests?: ClientRequest[];
  clientChat?: ClientMessage[];
  /** SitePhoto id (category "render") shown as the project's cover image. */
  mainRenderId?: string;
  /** How that render is framed in the hero band — a render is rarely composed
      for a 3:1 crop, so the focal point and zoom are the studio's to set. */
  mainRenderCrop?: RenderCrop;
  /** Hand-set position on the Projects page (lower first). Unset projects
      follow in creation order after the ordered ones. */
  listOrder?: number;
  /** Coordination models the contractor can open in the browser. */
  models?: BimModel[];
  /** The schedule of values the valuations are measured against. */
  sov?: SovLine[];
  valuations?: Valuation[];
  billing?: BillingDetails;
  expenses?: Expense[];
  /** Permits, insurances, bonds, warranties — everything that isn't a drawing. */
  documents?: ProjectDocument[];
  /** The DWG / model originals behind the drawings, each with every version
      it was updated with. */
  cadFiles?: CadFile[];
  /** Plant, tools, instruments, keys, devices and stock, with who holds each. */
  inventory?: InventoryItem[];
  /** Time-weighted % complete, one reading per day the programme moved —
      so "how much did we gain this fortnight" has an answer. */
  progressTrail?: { id: string; d: string; v: number }[];   // id = the day, so devices union rather than overwrite
  /** Letterhead marks carried onto every report issued from this project. */
  branding?: ProjectBranding;
  /** Roles this project defined for itself, beyond the built-in five. */
  customRoles?: CustomRole[];
  /** Changes to what the built-in roles may do on this project. */
  roleOverrides?: RoleOverrides;
}

export interface Tender {
  id: string;
  code: string;              // TDR-2601
  name: string;
  location: string;
  description: string;
  hue: number;               // plate/avatar hue
  status: TenderStatus;
  currency: string;
  estimate?: number;         // pre-tender estimate
  client: Party & { passcode: string };
  issueDate: string;
  closeDate: string;         // ISO with time — drives the countdown
  timeline: TimelineEvent[];
  docs: TenderDoc[];
  bidders: Bidder[];
  bids: Bid[];
  rfis: Rfi[];
  /** "sealed" (default): amounts hidden from everyone until formal opening.
      "open": the manager sees bids as they arrive — no opening ceremony. */
  bidMode?: "sealed" | "open";
  coverId?: string;        // project render in the sheet store — card + dashboard cover
  rfqs?: QuotePackage[];   // supply quotation packages priced separately from the main tender
  team?: BidTeamMember[];  // studio-side tender team (admin / coordinator / viewer)
  clientTeam?: ClientSeat[];  // client-side people, each with their own code + role
  itt?: InvitationToTender;
  contract?: DraftContract;
  alerts: EmailAlert[];
  activity: Activity[];
  resultsSharedWithClient: boolean;
  awardedBidderId?: string;
}
